import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { formatDate, formatFileSize, formatOrderAmount, formatUsdFull, formatWib, type BudgetRowRef, type ProjectRef } from "@po/shared";
import { api, ApiError, recordPath, type AddressDetail, type CompanyDetail, type Freshness, type Job, type JobProgress, type PoDetail, type ReqDetail, type RfqComparison, type RfqDetail } from "../lib/api";
import { int, spaced } from "../lib/format";
import { useUserPref } from "../lib/prefs";
import { CriticalChip, Flags, LineChip } from "./badges";
import { ColumnPicker, progressText } from "./DataTable";
import { BudgetLineBody } from "./BudgetLineDrawer";
import { AutoCountLineBody, type AutoCountLineRef } from "./AutoCountLineDrawer";
import { ProjectBody } from "./ProjectDrawer";
import { BudgetsAsOf, usd } from "./budget";
import { ProgressBar } from "./ProgressBar";
import { NavTree } from "./NavTree";
import { PositionBody } from "./PositionDrawer";
import { ItemBody } from "./ItemDrawer";
import { LineGrid, type GridCol } from "./LineGrid";

/**
 * A requisition, a PO, an RFQ, an RFQ's quotation comparison, a line of a vessel's budget
 * sheet in a period (SPEC §8.2a, §8.6, §9), one position of a requisition or PO, or an item.
 */
export type DrawerRef =
  | { type: "requisition" | "po" | "rfq" | "rfq-compare" | "address" | "company"; no: string }
  | {
      type: "budget-line";
      row: BudgetRowRef;
      period: string;
      /** The rows of the list it was opened from, in order, for Previous and Next. */
      steps?: BudgetLineStep[];
      /** Opened from an Other cost figure: the ledger shows only the POs under approval at first. */
      otherCostOnly?: boolean;
    }
  | {
      type: "autocount-line";
      line: AutoCountLineRef;
      period: string;
      /** The lines of the SEA or PEA section it was opened from, in order, for Previous and Next. */
      steps?: AutoCountLineRef[];
    }
  | {
      type: "project";
      ref: ProjectRef;
      period: string;
      /** The rows of the Projects list, in order, for Previous and Next. */
      steps?: ProjectStep[];
    }
  | { type: "position"; recordType: "requisition" | "po" | "rfq"; recordNo: string; positionNo: number }
  | { type: "item"; no: string }
  | { type: "attachment"; recordType: AttachmentRecordType; recordNo: string; documentNo: string; fileName: string | null };

/** One row a budget line drawer can step to. */
export type BudgetLineStep = { row: BudgetRowRef; period: string };
/** One row of the Projects list a project drawer can step to. */
export type ProjectStep = { ref: ProjectRef; period: string };

type AttachmentRecordType = "requisition" | "po" | "rfq";

const drawerLabel = (r: DrawerRef) =>
  r.type === "budget-line" || r.type === "autocount-line"
    ? "Budget line"
    : r.type === "project"
      ? `Project ${r.ref.projectNo ?? "none"}`
    : r.type === "item"
      ? `Item ${r.no}`
    : r.type === "attachment"
      ? `Attachment ${r.fileName ?? r.documentNo}`
      : r.type === "position"
        ? `Position ${r.recordNo}/${r.positionNo}`
        : `${{ po: "PO", rfq: "RFQ", "rfq-compare": "Calculate purchase order for RFQ", requisition: "Requisition", address: "Address", company: "Company" }[r.type]} ${r.no}`;

export function Drawer({
  current,
  behind,
  canGoBack,
  onBack,
  onClose,
  onOpen,
  onReplace,
  freshness,
}: {
  current: DrawerRef;
  /** The page under the top of the stack: shown behind a file preview pop-up. */
  behind?: DrawerRef;
  canGoBack: boolean;
  onBack: () => void;
  onClose: () => void;
  onOpen: (r: DrawerRef) => void;
  /** Swaps the drawer shown for another, without adding to Back. */
  onReplace: (r: DrawerRef) => void;
  freshness?: Freshness;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Records and Calculate PO are full-screen pages, as in Sertica; a file preview pops up over the
  // page it was opened from; a budget line or a position is a side drawer, over its page too.
  const popup = current.type === "attachment";
  const isPage = (r: DrawerRef) => r.type !== "attachment" && r.type !== "budget-line" && r.type !== "autocount-line" && r.type !== "project" && r.type !== "position" && r.type !== "item";
  const page = isPage(current);
  // A pop-up or side drawer opened from a page keeps that page open behind it and closes back to
  // it; a page's ✕ closes the whole stack.
  const over = !page && !!behind && isPage(behind);
  const dismiss = over ? onBack : onClose;
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && dismiss();
    document.addEventListener("keydown", esc);
    panel.current?.focus();
    return () => document.removeEventListener("keydown", esc);
  }, [dismiss, current]);
  // The drawer scrolls itself: the dashboard behind it does not, so there is one scrollbar.
  useEffect(() => {
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = before;
    };
  }, []);
  const body = (r: DrawerRef) =>
    r.type === "budget-line" ? (
      <BudgetLineBody row={r.row} period={r.period} otherCostOnly={r.otherCostOnly} onOpen={onOpen} steps={r.steps} onStep={(st) => onReplace({ ...r, row: st.row, period: st.period })} />
    ) : r.type === "autocount-line" ? (
      <AutoCountLineBody line={r.line} period={r.period} onOpen={onOpen} steps={r.steps} onStep={(line) => onReplace({ ...r, line })} />
    ) : r.type === "project" ? (
      <ProjectBody r={r.ref} period={r.period} onOpen={onOpen} steps={r.steps} onStep={(st) => onReplace({ ...r, ref: st.ref, period: st.period })} />
    ) : r.type === "position" ? (
      <PositionBody recordType={r.recordType} recordNo={r.recordNo} positionNo={r.positionNo} onOpen={onOpen} />
    ) : r.type === "item" ? (
      <ItemBody no={r.no} onOpen={onOpen} />
    ) : r.type === "requisition" ? (
      <RequisitionBody no={r.no} onOpen={onOpen} freshness={freshness} />
    ) : r.type === "rfq" ? (
      <RfqBody no={r.no} onOpen={onOpen} freshness={freshness} />
    ) : r.type === "attachment" ? (
      <AttachmentBody a={r} freshness={freshness} />
    ) : r.type === "rfq-compare" ? (
      <RfqCompareBody no={r.no} onOpen={onOpen} freshness={freshness} />
    ) : r.type === "address" ? (
      <AddressBody no={r.no} onOpen={onOpen} freshness={freshness} />
    ) : r.type === "company" ? (
      <CompanyBody no={r.no} onOpen={onOpen} freshness={freshness} />
    ) : (
      <PoBody no={r.no} onOpen={onOpen} freshness={freshness} />
    );
  return (
    <div className={`fixed inset-0 z-40 flex ${popup ? "items-center justify-center p-4" : "justify-end"}`} role="dialog" aria-modal="true" aria-label={drawerLabel(current)}>
      {/* The page a pop-up or side drawer was opened from stays in place behind it, inert. */}
      {over && (
        <div className="absolute inset-0 bg-card overflow-hidden pointer-events-none" aria-hidden>
          <div className="h-[41px] border-b border-line" />
          {body(behind!)}
        </div>
      )}
      <div className={`absolute inset-0 ${popup ? "bg-black/50" : "bg-black/30"}`} onClick={dismiss} />
      <div
        ref={panel}
        tabIndex={-1}
        className={`relative bg-card overflow-y-auto outline-none ${
          popup ? "w-full max-w-[1000px] h-[88vh] rounded-card border border-line shadow-2xl" : page ? "w-full h-full" : `w-full ${current.type === "position" || current.type === "item" ? "md:w-[min(1040px,94vw)]" : current.type === "budget-line" || current.type === "autocount-line" || current.type === "project" ? "md:w-[min(820px,94vw)]" : "md:w-[480px]"} h-full border-l border-line shadow-2xl`
        }`}
      >
        <div className="sticky top-0 bg-card border-b border-line px-4 py-2 flex items-center gap-2 z-10">
          {/* A page's Back goes to the page it was opened from, else back to the list; a pop-up has only ✕. */}
          {!popup && (canGoBack || page) && (
            <button onClick={canGoBack ? onBack : onClose} className="text-[13px] text-brand font-semibold">
              ← Back
            </button>
          )}
          <span className="flex-1" />
          <button onClick={dismiss} aria-label="Close" className="text-[18px] text-muted hover:text-ink px-1">
            ✕
          </button>
        </div>
        {body(current)}
      </div>
    </div>
  );
}

/** "Copy number" in a record's navy action bar. */
function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          // clipboard blocked: the number stays selectable
        }
      }}
    >
      <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="8" y="8" width="12" height="12" rx="1.5" />
        <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
      </svg>
      {done ? "Copied" : "Copy number"}
    </button>
  );
}

/** A white panel with a heading, as Sertica groups a record's fields. */
const Panel = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="card p-3">
    <h3 className="text-[14px] font-medium mb-2">{title}</h3>
    {children}
  </section>
);

/** A read-only field drawn like Sertica's form boxes: the label small, inside the outline. */
const BoxField = ({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) => (
  <div className={`rounded-input border border-line px-2 pt-1 pb-1.5 min-h-[42px] ${wide ? "col-span-2" : ""}`}>
    <div className="text-[10.5px] text-muted">{label}</div>
    <div className="text-[13px] text-ink break-words">{children || "—"}</div>
  </div>
);

type RefreshState = "idle" | "refreshing" | "waiting" | "unavailable" | "failed" | "done";

type Refresh = ReturnType<typeof useRefresh>;

/**
 * "Refresh from Sertica" (SPEC §11.3): a refresh job for one record, followed to its end. The
 * data stays visible in every state. With `auto`, a fetch starts on open when none is running:
 * for data only ever read on demand.
 */
function useRefresh(type: "requisition" | "po" | "rfq" | "address" | "company" | "attachment", no: string, onDone: () => void, freshness?: Freshness, auto = false) {
  const [state, setState] = useState<RefreshState>("idle");
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const [progress, setProgress] = useState<JobProgress | null>(null);
  /** The worker's reason when a fetch failed, such as a unit the service account cannot read. */
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval>>();
  useEffect(() => () => clearInterval(timer.current), []);
  const asOf = freshness?.asOf ? formatWib(new Date(freshness.asOf)) : "the last sync";

  // A job may still be open from before the drawer was closed: keep showing it.
  useEffect(() => {
    void api<Job | null>(`/refresh/open?type=${type}&no=${encodeURIComponent(no)}`).then((j) => {
      if (j) follow(j, new Date(j.createdAt).getTime());
    });
  }, [type, no]);
  // `auto` may turn true only once the caller knows the data is missing (it loads after mount):
  // start then, once per record.
  const autoStarted = useRef<string | null>(null);
  useEffect(() => {
    const key = `${type}:${no}`;
    if (!auto || autoStarted.current === key) return;
    autoStarted.current = key;
    void api<Job | null>(`/refresh/open?type=${type}&no=${encodeURIComponent(no)}`).then((j) => {
      if (j) follow(j, new Date(j.createdAt).getTime());
      else void start();
    });
  }, [type, no, auto]);

  async function start() {
    setState("refreshing");
    setProgress(null);
    clearInterval(timer.current);
    let job: Job;
    try {
      job = await api<Job>("/refresh", { method: "POST", body: JSON.stringify({ type, no }) });
    } catch {
      return setState("failed");
    }
    follow(job, Date.now());
  }

  function follow(job: Job, started: number) {
    setState("refreshing");
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const elapsed = Date.now() - started;
      let j: Job;
      try {
        j = await api<Job>(`/refresh/${job.id}`, { background: true });
      } catch {
        return;
      }
      setProgress(j.progress ?? null);
      if (j.state === "done") {
        clearInterval(timer.current);
        setFetchedAt(j.finishedAt ? new Date(j.finishedAt) : new Date());
        setState("done");
        onDone();
      } else if (j.state === "failed") {
        clearInterval(timer.current);
        setError(j.error);
        setState("failed");
      } else if (j.state === "queued" && elapsed > 10_000) {
        clearInterval(timer.current);
        setState("unavailable");
      } else if (elapsed > 60_000) {
        clearInterval(timer.current);
        setError("it took longer than a minute; try again");
        setState("failed");
      } else if (j.state === "running" && elapsed > 10_000) setState("waiting");
    }, 1000);
  }

  return { state, fetchedAt, progress, error, asOf, start, busy: state === "refreshing" || state === "waiting" };
}

/** The refresh icon at the drawer header's right; its name shows on hover. */
function RefreshIcon({ r, label = "Refresh from Sertica" }: { r: Refresh; label?: string }) {
  return (
    <button
      className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-input border border-line text-brand hover:bg-hover disabled:opacity-60"
      disabled={r.busy}
      onClick={r.start}
      title={r.busy ? "Fetching from Sertica…" : label}
      aria-label={r.busy ? "Fetching from Sertica…" : label}
    >
      <svg aria-hidden viewBox="0 0 24 24" className={`w-4 h-4 ${r.busy ? "animate-spin" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <path d="M21 3v6h-6" />
      </svg>
    </button>
  );
}

/** What a refresh is doing: its progress bar, then how it ended. Nothing while idle. */
function RefreshStatus({ r }: { r: Refresh }) {
  return (
    <>
      {r.busy && <ProgressBar progress={r.progress} label="Asking the sync service…" className="max-w-[320px]" />}
      <p className="text-[12px] empty:hidden" role="status">
        {r.state === "waiting" && <span className="text-muted">Still waiting for Sertica…</span>}
        {r.state === "unavailable" && <span className="text-[var(--danger)]">Refresh unavailable: sync service is not running. Showing data as of {r.asOf}.</span>}
        {r.state === "failed" && (
          <span className="text-[var(--danger)]">
            {r.error ? `Couldn't fetch from Sertica: ${r.error}.` : `Couldn't reach Sertica. Showing data as of ${r.asOf}.`}{" "}
            <button className="underline font-semibold" onClick={r.start}>
              Retry
            </button>
          </span>
        )}
        {r.state === "done" && r.fetchedAt && <span className="text-[var(--stage-received)]">✓ Fetched from Sertica at {formatWib(r.fetchedAt)}</span>}
      </p>
    </>
  );
}

function Quarantine({ since }: { since: string | null }) {
  if (!since) return null;
  return (
    <div role="alert" className="mx-4 mb-2 rounded-input bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 text-[12.5px] px-3 py-2">
      This record failed to sync since {formatWib(new Date(since))}
    </div>
  );
}

/**
 * The Over budget block (SPEC §9): each over-budget line the record feeds, judged in its quarter,
 * with a link to that line in Budgets. Absent without budget access or when nothing is over.
 */
function OverBudgetBlock({ o, freshness, onOpen }: { o: ReqDetail["overBudget"]; freshness?: Freshness; onOpen: (r: DrawerRef) => void }) {
  if (!o?.lines.length) return null;
  return (
    <section aria-label="Over budget" className="mx-4 mb-2 rounded-input border px-3 py-2 space-y-1.5" style={{ borderColor: "var(--flag-over_budget)" }}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h3 className="label flex-1" style={{ color: "var(--flag-over_budget)" }}>
          Over budget
        </h3>
        <BudgetsAsOf f={freshness} />
      </div>
      <ul className="space-y-1.5 text-[12.5px]">
        {o.lines.map((l) => (
          <li key={l.key}>
            <button
              className="text-brand font-semibold hover:underline text-left"
              onClick={() =>
                onOpen({
                  type: "budget-line",
                  row: { unitNo: l.unitNo, row: "line", purchaseTypeNo: l.purchaseTypeNo, technicalAccountNo: l.technicalAccountNo },
                  period: `${l.year}-Q${l.quarter}`,
                })
              }
            >
              {[l.unitName ?? l.unitNo, l.purchaseTypeNo, l.technicalAccountNo, `Q${l.quarter} ${l.year}`].join(" · ")}
            </button>
            {/* This quarter's room on the line against this record's value on it (SPEC §3.2). */}
            <div className="text-muted tnum">
              {l.budgetUsd === 0 ? "budget $0" : `budget ${usd(l.budgetUsd)}`} · committed + accrued {usd(l.usedUsd)} · remaining {usd(l.remainingUsd)} · this
              record {usd(l.valueUsd)} · <span className="neg">short by {usd(l.shortUsd)}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The Over budget notice under a requisition's estimated value (SPEC §3.2, §9): how much it is short by, over the budget lines it does not fit. */
function OverBudgetNotice({ o, freshness }: { o: ReqDetail["overBudget"]; freshness?: Freshness }) {
  if (!o?.lines.length) return null;
  return (
    <span role="alert" className="block text-[12px] whitespace-nowrap overflow-hidden text-ellipsis">
      <span className="neg tnum">short by {usd(o.lines.reduce((t, l) => t + l.shortUsd, 0))}</span> <BudgetsAsOf f={freshness} />
    </span>
  );
}

/** Marks a line on a budget line the record does not fit with the flag's colour (SPEC §9). */
const feedsMark = (feeds: Set<number>, positionNo: number) =>
  feeds.has(positionNo) ? { style: { boxShadow: "inset 3px 0 0 var(--flag-over_budget)" }, title: "On a budget line this record does not fit" } : {};

/**
 * A record page fetches from Sertica on open, unless it was fetched within the last delta interval
 * (back, a file preview), or the record is closed and auto sync is on (the schedule keeps those
 * current; with it off, nothing else would).
 */
const FETCH_ON_OPEN_AFTER_MS = 5 * 60_000;
const fetchOnOpen = (fetchedAt: string | null, closed: boolean, freshness?: Freshness) =>
  !(closed && freshness?.scheduled !== false) && (!fetchedAt || Date.now() - new Date(fetchedAt).getTime() > FETCH_ON_OPEN_AFTER_MS);
const CLOSED_REQ = new Set(["received", "rejected"]);
const CLOSED_PO = new Set(["received", "cancelled"]);
const CLOSED_RFQ = new Set(["Ordered", "Rejected", "Ignored"]);

/**
 * After a refresh: the open pages reload now; the lists behind them are only marked stale, so they
 * do not refetch (and dim) under the page, and reload on their next use.
 */
const reloadRecords = (qc: QueryClient) =>
  Promise.all([
    qc.invalidateQueries({ queryKey: ["record"] }),
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "record", refetchType: "none" }),
  ]);

function useRecord<T>(path: string) {
  return useQuery({ queryKey: ["record", path], queryFn: () => api<T>(path) });
}

/**
 * A record page's content with Sertica's Navigation beside it on the right, kept in view while
 * the page scrolls; below the content on a narrow screen.
 */
const WithNav = ({ nav, children }: { nav: ReactNode; children: ReactNode }) => (
  <div className="p-3 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(340px,30%)] lg:gap-3 lg:items-start">
    <div className="space-y-3 min-w-0">{children}</div>
    <aside className="mt-3 lg:mt-0 lg:sticky lg:top-12 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto">{nav}</aside>
  </div>
);

/** An amount in its own currency, as Sertica shows it; the bare number when the currency is not known. */
const amountIn = (a: { value: number; currencyNo: string | null }) => (a.currencyNo ? formatOrderAmount(a.value, a.currencyNo) : int(a.value));

function RequisitionBody({ no, onOpen, freshness }: { no: string; onOpen: (r: DrawerRef) => void; freshness?: Freshness }) {
  const qc = useQueryClient();
  const { data: r, isLoading, isError, error } = useRecord<ReqDetail>(recordPath("requisition", no));
  // Fetched from Sertica on open, so the page shows what Sertica has now (and the user numbers).
  const refresh = useRefresh("requisition", no, () => void reloadRecords(qc), freshness, !!r && (!r.usersRead || !r.refsRead || fetchOnOpen(r.fetchedAt, CLOSED_REQ.has(r.stage), freshness)));
  if (isLoading) return <div className="p-4 text-muted">Loading…</div>;
  if (isError || !r) return <NotInStore type="requisition" no={no} error={error} freshness={freshness} />;
  const progress = progressText(r.stage, r.progress.lines, r.progress.ordered, r.progress.received);
  const feeds = new Set(r.overBudget?.positions);
  const sec = (s: string) => `req-${s}`;
  return (
    <div className="bg-hover min-h-full pb-4">
      {/* Sertica's record title bar, then its navy action bar (here: the dashboard's read-only actions). */}
      <div className="flex items-center gap-2 px-4 h-11 bg-card">
        <span className="text-brand-2" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="20" r="1.5" />
            <circle cx="18" cy="20" r="1.5" />
            <path d="M2 3h3l2.7 12.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L21 7H6" />
          </svg>
        </span>
        <span className="text-[14px] font-bold uppercase tracking-[0.02em]">Requisition</span>
      </div>
      <div className="toolbar flex items-center gap-1 px-3 h-10">
        <CopyButton text={r.no} />
        <button disabled={refresh.busy} onClick={refresh.start}>
          <svg aria-hidden viewBox="0 0 24 24" className={`w-4 h-4 ${refresh.busy ? "animate-spin" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v6h-6" />
          </svg>
          {refresh.busy ? "Fetching from Sertica…" : "Refresh from Sertica"}
        </button>
      </div>
      <WithNav
        nav={
          <div id={sec("nav")} className="scroll-mt-12">
            <NavTree kind="requisition" record={r} onOpen={onOpen} />
          </div>
        }
      >
        {/* Each fact once: number, unit, delivery date and Sertica's status on top; the panels below hold the rest. */}
        <section className="card">
          <div className="px-4 pt-3 pb-2 space-y-1.5 text-[13px]">
            <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 flex-1 min-w-0">
                <h2 className="text-[17px] text-ink tnum">{r.no}</h2>
                {(r.unitNo || r.vessel) && (
                  <span className="flex items-center gap-1.5 text-ink2" title="Unit">
                    <svg aria-hidden width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted">
                      <rect x="3" y="3" width="6" height="6" rx="1" />
                      <rect x="15" y="15" width="6" height="6" rx="1" />
                      <path d="M6 9v4a2 2 0 0 0 2 2h7" />
                    </svg>
                    {[r.unitNo, r.vessel].filter(Boolean).join(" - ")}
                  </span>
                )}
                {r.requestedDeliveryDate && (
                  <span className="flex items-center gap-1.5 text-ink2" title="Requested delivery date">
                    <svg aria-hidden width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-muted">
                      <rect x="3.5" y="5" width="17" height="15" rx="1.5" />
                      <path d="M3.5 10h17M8 3v4M16 3v4" />
                    </svg>
                    {formatDate(r.requestedDeliveryDate)}
                  </span>
                )}
                {/* One chip, as the table's column: Sertica's status, then its positions' workflows. */}
                <span
                  className="rounded-badge px-2 py-0.5 text-[12px] font-medium"
                  style={{ background: "color-mix(in srgb, var(--brand) 16%, transparent)", color: "var(--brand-2)" }}
                  title="Status - Position workflow in Sertica"
                >
                  {[spaced(r.status), spaced(r.workflow)].filter(Boolean).join(" - ")}
                </span>
                {progress && <span className="text-[12px] text-muted">{progress}</span>}
                <Flags row={r.flags} />
              </div>
              <div className="text-right space-y-1">
                <RefreshStatus r={refresh} />
                {/* Once, either way: the refresh just done says it itself. */}
                {r.fetchedAt && refresh.state !== "done" && <p className="text-[11.5px] text-muted">Fetched from Sertica at {formatWib(new Date(r.fetchedAt))}</p>}
              </div>
            </div>
            {r.description && <p className="text-[15px] text-ink">{r.description}</p>}
            {r.requisitionType && <p className="text-ink2">{spaced(r.requisitionType)}</p>}
          </div>
        </section>
        {/* Sertica's General and Accounting Dimensions panels; what the header shows is not repeated. */}
        <div className="grid md:grid-cols-2 gap-3">
          <Panel title="General">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="Company">
                <CompanyLink no={r.companyNo} name={r.companyName} onOpen={onOpen} />
              </BoxField>
              <BoxField label="Urgent">{r.urgent ? "Yes" : "No"}</BoxField>
              <BoxField label="Department">{r.department ?? r.departmentNo}</BoxField>
              <BoxField label="Create date">{r.createDate && formatDate(r.createDate)}</BoxField>
              <BoxField label="Calculated price">{r.calculatedPrice && amountIn(r.calculatedPrice)}</BoxField>
              <BoxField label="Est. value (USD)">
                {/* Two lines: the value, then the Over budget notice. */}
                <span className="block whitespace-nowrap overflow-hidden text-ellipsis" title="Ordered lines at their PO price, the rest at the item's last PO price in the same unit (any count unit — Pieces, Sets, Unit… — for a counted line).">
                  {r.estValueUsd == null ? "—" : formatUsdFull(r.estValueUsd)}
                  {r.estLines > 0 && r.estLinesPriced < r.estLines && (
                    <span className="text-muted text-[12px]">
                      {" "}
                      · {r.estLinesPriced} of {r.estLines} lines priced
                    </span>
                  )}
                </span>
                <OverBudgetNotice o={r.overBudget} freshness={freshness} />
              </BoxField>
            </div>
          </Panel>
          <Panel title="Accounting dimensions">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="Purchase type">{r.purchaseType ?? r.purchaseTypeNo}</BoxField>
              <BoxField label="Project">
                {r.projectNo && (r.projectName ? `${r.projectNo} · ${r.projectName}` : r.projectNo)}
              </BoxField>
              {/* This quarter's budget of each budget line its lines are coded to; only with budget access (SPEC §12). */}
              {r.budget?.map((b) => (
                <BoxField key={`${b.purchaseTypeNo}|${b.technicalAccountNo}`} label={`Budget · ${b.technicalAccountNo ?? "no technical account"} · Q${b.quarter} ${b.year}`} wide>
                  {b.budgetUsd === null ? (
                    <span className="text-muted">No budget line for this coding</span>
                  ) : (
                    <>
                      <span className="grid grid-cols-4 gap-2 tnum">
                        <span>
                          <span className="block text-[11px] text-muted">Budget</span>
                          {formatUsdFull(b.budgetUsd)}
                        </span>
                        <span title="Ordered on this budget line this quarter: committed + accrued, this requisition's own left out">
                          <span className="block text-[11px] text-muted">Actual</span>
                          {formatUsdFull(b.reservedUsd ?? 0)}
                        </span>
                        {/* Opens the budget line, whose ledger lists each PO under approval (RESERVE). */}
                        <button
                          className="text-left"
                          title="Reserved by POs under approval (not ordered yet) on this budget line, this requisition's own left out. Click to see the POs"
                          onClick={() =>
                            onOpen({
                              type: "budget-line",
                              row: { unitNo: b.unitNo, row: "line", purchaseTypeNo: b.purchaseTypeNo, technicalAccountNo: b.technicalAccountNo },
                              period: `${b.year}-Q${b.quarter}`,
                              otherCostOnly: true,
                            })
                          }
                        >
                          <span className="block text-[11px] text-muted">Other cost</span>
                          <span className="text-brand font-semibold hover:underline">{formatUsdFull(b.otherCostUsd ?? 0)}</span>
                        </button>
                        <span title="Budget − actual − other cost">
                          <span className="block text-[11px] text-muted">Remaining</span>
                          <span className={(b.remainingUsd ?? 0) < 0 ? "neg" : undefined}>{formatUsdFull(b.remainingUsd ?? 0)}</span>
                        </span>
                      </span>
                    </>
                  )}
                </BoxField>
              ))}
            </div>
          </Panel>
          {/* Sertica's Remarks tab; left out when both are empty. */}
          {(r.remarkToOffice || r.remarkToSupplier) && (
            <div className="md:col-span-2">
              <Panel title="Remarks">
                <div className="grid grid-cols-2 gap-2">
                  <BoxField label="Remark to office">
                    {r.remarkToOffice && <span className="whitespace-pre-line">{r.remarkToOffice}</span>}
                  </BoxField>
                  <BoxField label="Remark to supplier">
                    {r.remarkToSupplier && <span className="whitespace-pre-line">{r.remarkToSupplier}</span>}
                  </BoxField>
                </div>
              </Panel>
            </div>
          )}
          <ReqRefsPanels r={r} />
        </div>
        {/* The Over budget notice is in the Budget boxes of Accounting dimensions. */}
        <Quarantine since={r.quarantinedSince} />
        <section className="card scroll-mt-12" id={sec("positions")} aria-label="Positions">
          <ReqLines r={r} feeds={feeds} onOpen={onOpen} />
        </section>
      </WithNav>
    </div>
  );
}

type ReqLine = ReqDetail["lines"][number];
type LineCol = {
  key: string;
  label: string;
  right?: boolean;
  cell: (l: ReqLine) => ReactNode;
  /** The picker's heading it sits under; none for the common columns. */
  group?: string;
  /** What the column sorts by; a Position Details field's own value when left out. */
  value?: (l: ReqLine) => string | number | boolean | null | undefined;
};

const qtyFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 });
/** One of Sertica's Position Details fields; "—" until the line is read whole. */
const detail = (key: string, label: string, kind: "text" | "qty" | "date" = "text", rare = false): LineCol => ({
  key,
  label,
  ...(rare ? { group: "Usually empty" } : {}),
  right: kind === "qty",
  cell: (l) => {
    const v = l.details?.[key];
    if (v == null || v === "") return "—";
    if (kind === "qty") return typeof v === "number" ? qtyFmt.format(v) : String(v);
    if (kind === "date") return formatDate(String(v));
    return String(v);
  },
});

/**
 * Every column the requisition's position grid can show: Sertica's grid and Position Details. The
 * first ten are the default; the rest wait for the user to tick them.
 */
const REQ_LINE_COLS: LineCol[] = [
  { key: "positionNo", label: "Position no.", right: true, value: (l) => l.positionNo, cell: (l) => l.positionNo },
  { key: "itemNo", label: "Item no.", value: (l) => l.itemNo, cell: (l) => l.itemNo ?? "—" },
  { key: "name", label: "Position name", value: (l) => l.name, cell: () => null },
  { key: "requestedQuantity", label: "Requested quantity", right: true, value: (l) => l.requestedQuantity, cell: (l) => (l.requestedQuantity != null ? int(l.requestedQuantity) : "—") },
  { key: "quantityToOrder", label: "Quantity to order", right: true, value: (l) => l.quantityToOrder, cell: (l) => (l.quantityToOrder != null ? int(l.quantityToOrder) : "—") },
  { key: "receivedQuantity", label: "Received quantity", right: true, value: (l) => l.receivedQuantity, cell: (l) => (l.receivedQuantity != null ? int(l.receivedQuantity) : "—") },
  // The line's estimate: its PO price, else quantity × the item's last price (SPEC §8.2).
  { key: "subtotalUsd", label: "Subtotal (USD)", right: true, value: (l) => l.subtotalUsd, cell: (l) => (l.subtotalUsd != null ? formatUsdFull(l.subtotalUsd) : "—") },
  { key: "unit", label: "Measuring unit", value: (l) => l.unit, cell: (l) => l.unit ?? "—" },
  { key: "remark", label: "Remark", value: (l) => l.remark, cell: (l) => l.remark ?? "—" },
  { key: "technicalAccountNo", label: "Technical account no.", value: (l) => l.technicalAccountNo, cell: (l) => l.technicalAccountNo ?? "—" },
  // Optional, in Sertica's grid order first.
  detail("orderedQuantity", "Ordered quantity", "qty"),
  detail("outstandingQuantity", "Outstanding quantity", "qty"),
  detail("requestedMeasureUnitNo", "Requested measure unit no."),
  detail("requestedMeasureUnitName", "Requested measure unit"),
  detail("measureUnitNo", "Measuring unit no."),
  { key: "price", label: "Price", right: true, value: (l) => l.details?.price, cell: (l) => (typeof l.details?.price === "number" ? qtyFmt.format(l.details.price) : "—") },
  detail("currencyNo", "Currency"),
  detail("discount", "Discount (%)", "qty"),
  { key: "priceUsd", label: "Price (USD)", right: true, value: (l) => l.details?.priceInStandardCurrency, cell: (l) => (typeof l.details?.priceInStandardCurrency === "number" ? formatUsdFull(l.details.priceInStandardCurrency) : "—") },
  { key: "totalUsd", label: "Total price (USD)", right: true, value: (l) => l.details?.totalPriceInStandardCurrency, cell: (l) => (typeof l.details?.totalPriceInStandardCurrency === "number" ? formatUsdFull(l.details.totalPriceInStandardCurrency) : "—") },
  { key: "technicalAccountName", label: "Technical account name", value: (l) => l.technicalAccountName, cell: (l) => l.technicalAccountName ?? "—" },
  { key: "workflow", label: "Workflow", value: (l) => l.workflow, cell: (l) => spaced(l.workflow) ?? "—" },
  detail("itemName", "Item name"),
  detail("itemTypeName", "Item type"),
  detail("itemGroupNoNames", "Item groups"),
  detail("itemReferenceNo", "Item reference no."),
  detail("internalRemark", "Internal remark"),
  detail("supplierItemNo", "Supplier item no."),
  detail("standardNo", "Standard no."),
  detail("manufacture", "Manufacturer"),
  detail("typeNo", "Type no."),
  detail("drawingNo", "Drawing no."),
  detail("drawingPosNo", "Drawing pos. no."),
  detail("referenceNo", "Reference no."),
  detail("referencePosNo", "Reference pos. no."),
  detail("dimensions", "Dimensions"),
  detail("classification", "Classification"),
  detail("stock", "Stock", "qty"),
  detail("minStock", "Min. stock", "qty"),
  detail("maxStock", "Max. stock", "qty"),
  detail("requestedDeliveryDate", "Requested delivery date", "date"),
  detail("deliveryDate", "Delivery date", "date"),
  detail("receivedDate", "Received date", "date"),
  detail("technicalApprovalDate", "Technical approval date", "date"),
  detail("technicalApprovalUserNo", "Technically approved by"),
  detail("hsCodeNo", "HS code"),
  detail("hsWeight", "HS weight", "qty"),
  // Rarely filled in Sertica (empty on 1,080 recent lines): listed last, under their own heading.
  detail("rejectedQuantity", "Rejected quantity", "qty", true),
  detail("supplierItemDescription", "Supplier item description", "text", true),
  detail("supplierReferenceNo", "Supplier reference no.", "text", true),
  detail("jobNo", "Job no.", "text", true),
  detail("jobName", "Job", "text", true),
  detail("deliveryNoteNo", "Delivery note no.", "text", true),
  detail("rejectReasonName", "Reject reason", "text", true),
  detail("rejectRemark", "Reject remark", "text", true),
];
const REQ_LINE_DEFAULT = REQ_LINE_COLS.slice(0, 10).map((c) => c.key);
const REQ_LINES_PREF = "requisition-lines";

/** A column's sort value: numbers by size, text by its digits too ("10" after "9"); empty last. */
const lineValue = (c: LineCol, l: ReqLine) => (c.value ? c.value(l) : l.details?.[c.key]);
function compareValues(a: unknown, b: unknown): number {
  if (a == null || a === "") return b == null || b === "" ? 0 : 1;
  if (b == null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/**
 * The requisition's position grid. Its columns and their order are the user's own, kept per user on
 * the server: ticked in "Columns", moved by dragging a header. A header click sorts by that column,
 * then reverses, then returns to position order; empty values stay last.
 */
function ReqLines({ r, feeds, onOpen }: { r: ReqDetail; feeds: Set<number>; onOpen: (r: DrawerRef) => void }) {
  const [pref, setPref] = useUserPref<string[]>(REQ_LINES_PREF);
  const known = (pref ?? REQ_LINE_DEFAULT).filter((k) => REQ_LINE_COLS.some((c) => c.key === k));
  const order = known.includes("positionNo") ? known : ["positionNo", ...known];
  const shown = new Set(order);
  const cols = order.map((k) => REQ_LINE_COLS.find((c) => c.key === k)!);
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const dragged = useRef<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const sortBy = (key: string) => setSort((s) => (s?.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : null));
  // Dropped on a column: placed where that column was, so it lands on the side it came from.
  const move = (key: string, onto: string) => {
    if (key === onto) return;
    const rest = order.filter((k) => k !== key);
    const at = rest.indexOf(onto) + (order.indexOf(key) < order.indexOf(onto) ? 1 : 0);
    setPref([...rest.slice(0, at), key, ...rest.slice(at)]);
  };
  const sortCol = sort && cols.find((c) => c.key === sort.key);
  const lines = sortCol
    ? [...r.lines].sort((a, b) => {
        const va = lineValue(sortCol, a);
        const vb = lineValue(sortCol, b);
        // Empty stays last either way; ties keep position order.
        const empty = (v: unknown) => v == null || v === "";
        if (empty(va) || empty(vb)) return compareValues(va, vb) || a.positionNo - b.positionNo;
        return compareValues(va, vb) * sort!.dir || a.positionNo - b.positionNo;
      })
    : r.lines;
  const sub = cols.findIndex((c) => c.key === "subtotalUsd");
  return (
    <>
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <h3 className="flex items-center gap-2 text-[17px] text-ink flex-1">
          Positions <span className="tnum rounded-full bg-hover border border-line px-2 text-[13px] leading-6">{r.lines.length}</span>
        </h3>
        <span className="text-[11.5px] text-muted hidden md:inline">Drag a column title to move it</span>
        <ColumnPicker
          options={REQ_LINE_COLS}
          shown={shown}
          locked="positionNo"
          className="text-[12.5px] text-brand border border-line rounded-input px-2 py-0.5"
          onChange={(key, on) => setPref(on ? [...order, key] : order.filter((k) => k !== key))}
          onReset={() => setPref(null)}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-line">
              {cols.map((c) => (
                <th
                  key={c.key}
                  draggable
                  onDragStart={(e) => {
                    dragged.current = c.key;
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    if (!dragged.current) return;
                    e.preventDefault();
                    setOver(c.key);
                  }}
                  onDragLeave={() => setOver((o) => (o === c.key ? null : o))}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragged.current) move(dragged.current, c.key);
                    dragged.current = null;
                    setOver(null);
                  }}
                  onDragEnd={() => {
                    dragged.current = null;
                    setOver(null);
                  }}
                  aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                  className={`col px-3 py-2 text-left first:pl-4 last:pr-4 cursor-grab ${c.right ? "text-right" : ""} ${over === c.key ? "bg-hover" : ""}`}
                >
                  <button className={`group inline-flex items-center gap-1 ${c.right ? "flex-row-reverse" : ""}`} onClick={() => sortBy(c.key)} title="Sort; drag to move">
                    {c.label}
                    <span aria-hidden className={sort?.key === c.key ? "text-ink" : "text-transparent group-hover:text-muted"}>
                      {sort?.key === c.key && sort.dir === -1 ? "▼" : "▲"}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.positionNo} className={`border-b border-line align-top ${l.rejected ? "text-muted" : "text-ink2"}`}>
                {cols.map((c) =>
                  c.key === "name" ? (
                    <td key={c.key} className="px-3 py-[5px] min-w-[220px]">
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <button className="text-left text-brand hover:underline" onClick={() => onOpen({ type: "position", recordType: "requisition", recordNo: r.no, positionNo: l.positionNo })}>
                          {l.name ?? "—"}
                        </button>
                        {l.critical && <CriticalChip remark={l.criticalRemark} />}
                        {l.details?.urgent === true && <LineChip kind="urgent" />}
                        {l.details?.linkedToOpenRFQPosition === true && <LineChip kind="open-rfq" />}
                      </span>
                    </td>
                  ) : (
                    <td
                      key={c.key}
                      className={`px-3 py-[5px] first:pl-4 last:pr-4 ${c.right ? "text-right tnum whitespace-nowrap" : ""} ${c.key.endsWith("emark") ? "min-w-[160px] whitespace-pre-line" : ""}`}
                      title={
                        c.key === "technicalAccountNo"
                          ? (l.technicalAccountName ?? undefined)
                          : c.key === "technicalApprovalUserNo" && typeof l.details?.technicalApprovalUserName === "string"
                            ? l.details.technicalApprovalUserName
                            : undefined
                      }
                      {...(c.key === "positionNo" ? feedsMark(feeds, l.positionNo) : {})}
                    >
                      {c.cell(l)}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
          {r.estValueUsd != null && sub >= 0 && (
            <tfoot>
              <tr className="font-semibold text-ink">
                {sub > 0 && (
                  <td colSpan={sub} className="px-3 py-[6px] pl-4 text-right">
                    Total{r.estLinesPriced < r.estLines && <span className="font-normal text-muted"> ({r.estLinesPriced} of {r.estLines} lines priced)</span>}
                  </td>
                )}
                <td className="px-3 py-[6px] text-right tnum whitespace-nowrap">{formatUsdFull(r.estValueUsd)}</td>
                {cols.length - sub - 1 > 0 && <td colSpan={cols.length - sub - 1} />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

const noName = (no: string | null, name: string | null) => [no, name].filter(Boolean).join(" - ");

/** A Components or PM/UPM (jobs) list: number and name. */
function RefList({ rows }: { rows: { no: string; name: string | null }[] }) {
  return (
    <ul className="text-[13px] divide-y divide-line max-h-72 overflow-y-auto">
      {rows.map((c) => (
        <li key={c.no} className="flex gap-3 py-1">
          <span className="tnum text-ink2 shrink-0">{c.no}</span>
          <span>{c.name ?? "—"}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Sertica's Components, Component Info and PM/UPM tabs, each left out when the requisition has
 * none. A list not read yet says so; opening the requisition reads it.
 */
function ReqRefsPanels({ r }: { r: ReqDetail }) {
  const notRead = <p className="text-[13px] text-muted">Not read from Sertica yet.</p>;
  return (
    <>
      {(r.components === null || r.components.length > 0 || r.componentInfo) && (
        <Panel title="Components">
          {r.components === null ? notRead : r.components.length > 0 && <RefList rows={r.components} />}
          {r.componentInfo && (
            <details className="mt-2">
              <summary className="text-[13px] text-brand cursor-pointer">Component info</summary>
              <pre className="mt-1 max-h-96 overflow-y-auto whitespace-pre-wrap font-sans text-[12.5px] text-ink2">{r.componentInfo}</pre>
            </details>
          )}
        </Panel>
      )}
      {(r.jobs === null || r.jobs.length > 0) && <Panel title="PM/UPM">{r.jobs === null ? notRead : <RefList rows={r.jobs} />}</Panel>}
    </>
  );
}

/**
 * Sertica's record title bar, then its navy action bar (here: the dashboard's read-only actions), as on the requisition page.
 * `source`: a record from elsewhere (a SEA or PEA PO), which has no Sertica refresh; where it is read from shows instead.
 */
function RecordBar({ icon, title, no, refresh, source, children }: { icon: ReactNode; title: string; no: string; refresh: Refresh; source?: string; children?: ReactNode }) {
  return (
    <>
      <div className="flex items-center gap-2 px-4 h-11 bg-card">
        <span className="text-brand-2" aria-hidden>
          {icon}
        </span>
        <span className="text-[14px] font-bold uppercase tracking-[0.02em]">{title}</span>
      </div>
      <div className="toolbar flex items-center gap-1 px-3 h-10">
        <CopyButton text={no} />
        {source ? (
          <span className="px-2 text-[12px]">{source}</span>
        ) : (
          <button disabled={refresh.busy} onClick={refresh.start}>
            <svg aria-hidden viewBox="0 0 24 24" className={`w-4 h-4 ${refresh.busy ? "animate-spin" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            {refresh.busy ? "Fetching from Sertica…" : "Refresh from Sertica"}
          </button>
        )}
        {children}
      </div>
    </>
  );
}

const iconProps = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const;
const PO_ICON = (
  <svg {...iconProps}>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
);
const RFQ_ICON = (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M14.8 9.2A3 3 0 0 0 12 8c-1.7 0-3 .9-3 2s1.3 1.7 3 2 3 .9 3 2-1.3 2-3 2a3 3 0 0 1-2.8-1.2M12 6v2M12 16v2" />
  </svg>
);

/**
 * The header card, as the requisition's: number, unit, a date and Sertica's status on top, the
 * fetch state on the right; then the record's own lines (description, supplier…).
 */
function RecordHeader({
  no,
  unit,
  date,
  dateTitle,
  status,
  statusTitle,
  extra,
  refresh,
  fetchedAt,
  children,
}: {
  no: string;
  unit: string;
  date: string | null;
  dateTitle: string;
  status: string;
  statusTitle: string;
  extra?: ReactNode;
  refresh: Refresh;
  fetchedAt: string | null;
  children?: ReactNode;
}) {
  return (
    <section className="card">
      <div className="px-4 pt-3 pb-2 space-y-1.5 text-[13px]">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 flex-1 min-w-0">
            <h2 className="text-[17px] text-ink tnum">{no}</h2>
            {unit && (
              <span className="flex items-center gap-1.5 text-ink2" title="Unit">
                <svg aria-hidden width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted">
                  <rect x="3" y="3" width="6" height="6" rx="1" />
                  <rect x="15" y="15" width="6" height="6" rx="1" />
                  <path d="M6 9v4a2 2 0 0 0 2 2h7" />
                </svg>
                {unit}
              </span>
            )}
            {date && (
              <span className="flex items-center gap-1.5 text-ink2" title={dateTitle}>
                <svg aria-hidden width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-muted">
                  <rect x="3.5" y="5" width="17" height="15" rx="1.5" />
                  <path d="M3.5 10h17M8 3v4M16 3v4" />
                </svg>
                {formatDate(date)}
              </span>
            )}
            {status && (
              <span
                className="rounded-badge px-2 py-0.5 text-[12px] font-medium"
                style={{ background: "color-mix(in srgb, var(--brand) 16%, transparent)", color: "var(--brand-2)" }}
                title={statusTitle}
              >
                {status}
              </span>
            )}
            {extra}
          </div>
          <div className="text-right space-y-1">
            <RefreshStatus r={refresh} />
            {/* Once, either way: the refresh just done says it itself. */}
            {fetchedAt && refresh.state !== "done" && <p className="text-[11.5px] text-muted">Fetched from Sertica at {formatWib(new Date(fetchedAt))}</p>}
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}

/** Sertica's Remarks tab; only when one is filled in. */
function PoRemarks({ p }: { p: PoDetail }) {
  const r = p.remarks;
  if (!r.toOffice && !r.toSupplier && !r.confirmation) return null;
  const text = (v: string | null) => v && <span className="whitespace-pre-line">{v}</span>;
  return (
    <div className="md:col-span-2">
      <Panel title="Remarks">
        <div className="grid grid-cols-2 gap-2">
          <BoxField label="Remarks to office">{text(r.toOffice)}</BoxField>
          <BoxField label="Remarks to supplier">{text(r.toSupplier)}</BoxField>
          {r.confirmation && (
            <BoxField label="Confirmation remarks" wide>
              {text(r.confirmation)}
            </BoxField>
          )}
        </div>
      </Panel>
    </div>
  );
}

/**
 * A Budget box, as the requisition's: the budget line's budget this quarter, its actual (committed +
 * accrued), the other cost of POs in approval (opening the budget line) and what remains, the
 * record's own left out.
 */
function BudgetBox({ b, own, onOpen }: { b: NonNullable<PoDetail["budget"]>[number]; own: string; onOpen: (r: DrawerRef) => void }) {
  return (
    <BoxField label={`Budget · ${b.technicalAccountNo ?? "no technical account"} · Q${b.quarter} ${b.year}`} wide>
      {b.budgetUsd === null ? (
        <span className="text-muted">No budget line for this coding</span>
      ) : (
        <span className="grid grid-cols-4 gap-2 tnum">
          <span>
            <span className="block text-[11px] text-muted">Budget</span>
            {formatUsdFull(b.budgetUsd)}
          </span>
          <span title={`Ordered on this budget line this quarter: committed + accrued, ${own} left out`}>
            <span className="block text-[11px] text-muted">Actual</span>
            {formatUsdFull(b.reservedUsd ?? 0)}
          </span>
          {/* Opens the budget line, whose ledger lists each PO under approval (RESERVE). */}
          <button
            className="text-left"
            title={`Reserved by POs under approval (not ordered yet) on this budget line, ${own} left out. Click to see the POs`}
            onClick={() =>
              onOpen({
                type: "budget-line",
                row: { unitNo: b.unitNo, row: "line", purchaseTypeNo: b.purchaseTypeNo, technicalAccountNo: b.technicalAccountNo },
                period: `${b.year}-Q${b.quarter}`,
                otherCostOnly: true,
              })
            }
          >
            <span className="block text-[11px] text-muted">Other cost</span>
            <span className="text-brand font-semibold hover:underline">{formatUsdFull(b.otherCostUsd ?? 0)}</span>
          </button>
          <span title="Budget − actual − other cost">
            <span className="block text-[11px] text-muted">Remaining</span>
            <span className={(b.remainingUsd ?? 0) < 0 ? "neg" : undefined}>{formatUsdFull(b.remainingUsd ?? 0)}</span>
          </span>
        </span>
      )}
    </BoxField>
  );
}

/** A SEA or PEA PO's Budget box: SEA's budget line in the quarter it counts this PO, in Sertica's four figures. */
function AutoCountBudgetBox({ b, onOpen }: { b: NonNullable<PoDetail["autoCountBudget"]>[number]; onOpen: (r: DrawerRef) => void }) {
  const open = () =>
    onOpen({ type: "autocount-line", line: { source: b.source, database: b.database, projectNo: b.projectNo, costItem: b.costItem, statusKey: b.statusKey }, period: `${b.year}-Q${b.quarter}` });
  const own = b.counted === "actual" ? `this PO's ${formatUsdFull(b.ownUsd)} included` : b.counted === "reserve" ? `this PO's ${formatUsdFull(b.ownUsd)} reserved here` : "this PO not counted yet (not transferred or reserved)";
  return (
    <BoxField label={["Technical account / Biaya", b.costItem, b.statusName, `Q${b.quarter} ${b.year}`].filter(Boolean).join(" · ")} wide>
      {b.budgetUsd === null ? (
        <span className="text-muted">No budget line for this coding</span>
      ) : (
        <span className="grid grid-cols-4 gap-2 tnum">
          <span title="Total budget less transfers out, as SEA's budget inquiry has it">
            <span className="block text-[11px] text-muted">Budget</span>
            {formatUsdFull(b.budgetUsd)}
          </span>
          <span title={`Transferred POs on this budget line this quarter, by submit date; ${own}`}>
            <span className="block text-[11px] text-muted">Actual</span>
            {formatUsdFull(b.actualUsd)}
          </span>
          {/* Opens the budget line, whose ledger lists each PO. */}
          <button className="text-left" title={`${b.source}'s reserve: POs not transferred yet on this budget line. Click to see the line's ledger`} onClick={open}>
            <span className="block text-[11px] text-muted">Other cost</span>
            <span className="text-brand font-semibold hover:underline">{formatUsdFull(b.otherCostUsd)}</span>
          </button>
          <span title="Budget − actual − other cost">
            <span className="block text-[11px] text-muted">Remaining</span>
            <span className={b.remainingUsd <= -1 ? "neg" : undefined}>{formatUsdFull(b.remainingUsd)}</span>
          </span>
        </span>
      )}
    </BoxField>
  );
}

type PoLine = PoDetail["lines"][number];

/** Every column the PO's position grid can show; the first ten are the default. */
const poLineCols = (p: PoDetail, onOpen: (r: DrawerRef) => void): GridCol<PoLine>[] => [
  { key: "positionNo", label: "Position no.", right: true, value: (l) => l.positionNo, cell: (l) => l.positionNo },
  {
    key: "name",
    label: "Position name",
    value: (l) => l.name,
    cell: (l) => (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <button className="text-left text-brand hover:underline" onClick={() => onOpen({ type: "position", recordType: "po", recordNo: p.no, positionNo: l.positionNo })}>
          {l.name ?? "—"}
        </button>
        {l.critical && <CriticalChip remark={l.criticalRemark} />}
      </span>
    ),
  },
  { key: "ordered", label: "Ordered quantity", right: true, value: (l) => l.ordered, cell: (l) => int(l.ordered) },
  { key: "received", label: "Received quantity", right: true, value: (l) => l.received, cell: (l) => int(l.received) },
  {
    key: "rejected",
    label: "Rejected quantity",
    right: true,
    value: (l) => l.rejected,
    cell: (l) => (l.rejected > 0 ? <span className="text-[var(--flag-rejected_on_receipt)] font-semibold">{int(l.rejected)}</span> : int(l.rejected)),
  },
  { key: "outstanding", label: "Outstanding quantity", right: true, value: (l) => l.outstanding, cell: (l) => int(l.outstanding) },
  { key: "unit", label: "Measuring unit", value: (l) => l.unit, cell: (l) => l.unit ?? "—" },
  { key: "unitPrice", label: "Price", right: true, value: (l) => l.unitPrice, cell: (l) => inCurrency(l.unitPrice, p.orderCurrency) },
  { key: "usdValue", label: "Total price (USD)", right: true, value: (l) => l.usdValue, cell: (l) => formatUsdFull(l.usdValue) },
  { key: "outstandingUsd", label: "Outstanding (USD)", right: true, value: (l) => l.outstandingUsd, cell: (l) => formatUsdFull(l.outstandingUsd) },
  // Optional.
  { key: "discount", label: "Discount (%)", right: true, value: (l) => l.discount, cell: (l) => (l.discount ? qtyFmt.format(l.discount) : "—") },
  { key: "totalPrice", label: "Total price", right: true, value: (l) => l.totalPrice, cell: (l) => inCurrency(l.totalPrice, p.orderCurrency) },
  { key: "accountNo", label: "Account no.", value: (l) => l.accountNo, cell: (l) => <span title={l.account ?? undefined}>{l.accountNo ?? "—"}</span> },
  { key: "account", label: "Account name", value: (l) => l.account, cell: (l) => l.account ?? "—" },
  { key: "latestReceivedDate", label: "Received date", value: (l) => l.latestReceivedDate, cell: (l) => (l.latestReceivedDate ? formatDate(l.latestReceivedDate) : "—") },
];
const PO_LINE_DEFAULT = ["positionNo", "name", "ordered", "received", "rejected", "outstanding", "unit", "unitPrice", "usdValue", "outstandingUsd"];

function PoBody({ no, onOpen, freshness }: { no: string; onOpen: (r: DrawerRef) => void; freshness?: Freshness }) {
  const qc = useQueryClient();
  const { data: p, isLoading, isError, error } = useRecord<PoDetail>(recordPath("po", no));
  // A SEA or PEA PO is read from there every hour; Sertica has none to fetch.
  const fromSertica = !p || p.source === "Sertica";
  const refresh = useRefresh("po", no, () => void reloadRecords(qc), freshness, !!p && fromSertica && (!p.usersRead || fetchOnOpen(p.fetchedAt, CLOSED_PO.has(p.stage), freshness)));
  if (isLoading) return <div className="p-4 text-muted">Loading…</div>;
  if (isError || !p) return <NotInStore type="po" no={no} error={error} freshness={freshness} />;
  const feeds = new Set(p.overBudget?.positions);
  return (
    <div className="bg-hover min-h-full pb-4">
      <RecordBar icon={PO_ICON} title="Purchase order" no={p.no} refresh={refresh} source={fromSertica ? undefined : `From ${p.source} · ${p.sourceDatabase} · read every hour`} />
      <WithNav
        nav={
          <div id="po-nav" className="scroll-mt-12">
            <NavTree kind="po" record={p} onOpen={onOpen} />
          </div>
        }
      >
        {/* Each fact once: number, unit, delivery date and Sertica's status on top; the panels below hold the rest. */}
        <RecordHeader
          no={p.no}
          unit={noName(p.unitNo, p.vessel)}
          date={p.deliveryDate}
          dateTitle="Delivery date"
          status={[spaced(p.orderStatus), spaced(p.orderWorkflow)].filter(Boolean).join(" - ")}
          statusTitle={fromSertica ? "Status - Workflow in Sertica" : `Status from ${p.source}'s goods receipts and invoices`}
          extra={<Flags row={p.flags} />}
          refresh={refresh}
          fetchedAt={p.fetchedAt}
        >
          {p.description && <p className="text-[15px] text-ink">{p.description}</p>}
          {p.requisitionType && <p className="text-ink2">{spaced(p.requisitionType)}</p>}
        </RecordHeader>
        {/* Sertica's General and Accounting Dimensions panels, then its Remarks; what the header shows is not repeated. */}
        <div className="grid md:grid-cols-2 gap-3">
          <Panel title="General">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="Supplier" wide>
                <SupplierLink no={p.supplierNo} name={p.supplier} onOpen={onOpen} />
              </BoxField>
              <BoxField label="Company">
                <CompanyLink no={p.companyNo} name={p.companyName} onOpen={onOpen} />
              </BoxField>
              <BoxField label="Urgent">{p.flags.urgent ? "Yes" : "No"}</BoxField>
              <BoxField label="Department">{p.department}</BoxField>
              <BoxField label="Order date">{p.orderDate && formatDate(p.orderDate)}</BoxField>
              <BoxField label="Order amount">{p.orderAmount != null && p.orderCurrency && formatOrderAmount(p.orderAmount, p.orderCurrency)}</BoxField>
              <BoxField label="Value (USD)">
                {/* Two lines: the value, then the Over budget notice. */}
                <span className="block whitespace-nowrap overflow-hidden text-ellipsis">{formatUsdFull(p.usdValue)}</span>
                <OverBudgetNotice o={p.overBudget} freshness={freshness} />
              </BoxField>
            </div>
          </Panel>
          <Panel title="Accounting dimensions">
            <div className="grid grid-cols-2 gap-2">
              {p.source === "Sertica" ? (
                <BoxField label="Purchase type">{p.purchaseTypes.map((t) => t.name ?? t.no).join(", ")}</BoxField>
              ) : (
                // A purchase type is SEA's or PEA's expense group (5. TRAVEL); a technical account its Biaya (TRAVEL - OPERATION).
                <BoxField label="Purchase type / expense group">
                  {p.autoCountPurchaseTypes.length ? (
                    p.autoCountPurchaseTypes.join(", ")
                  ) : p.budgetType && p.budgetType !== "Ship" ? (
                    <span title={`Its technical accounts are on no line of ${p.source}'s ship budget`}>
                      {p.budgetType} <span className="text-muted">· not on the ship budget</span>
                    </span>
                  ) : null}
                </BoxField>
              )}
              {p.source === "Sertica" ? (
                <BoxField label="Project">{p.projectNo && (p.projectName ? `${p.projectNo} · ${p.projectName}` : p.projectNo)}</BoxField>
              ) : (
                <BoxField label="Project">{p.autoCountProjects.join(", ")}</BoxField>
              )}
              {/* Without budget access the technical accounts stand alone; with it, each Budget box names its own. */}
              {p.source !== "Sertica" && !p.autoCountBudget && (
                <BoxField label={p.technicalAccounts.length > 1 ? "Technical accounts" : "Technical account"} wide>
                  {p.technicalAccounts.join(", ")}
                </BoxField>
              )}
              {/* This quarter's budget of each budget line its lines are placed on; only with budget access (SPEC §12). */}
              {p.source === "Sertica" && p.budget?.map((b) => <BudgetBox key={`${b.unitNo}|${b.purchaseTypeNo}|${b.technicalAccountNo}`} b={b} own="this PO's own" onOpen={onOpen} />)}
              {/* The vessel's SEA or PEA budget is a separate one: it does not count Sertica's POs, so its lines are not this PO's. */}
              {p.source === "Sertica" && !!p.budget?.length && p.otherBudgets.length > 0 && (
                <p className="col-span-2 text-[11px] text-muted">
                  Sertica&apos;s budget. {p.vessel ?? "This vessel"} also has {p.otherBudgets.map((o) => `a ${o.source} budget (${o.projectNo})`).join(" and ")}, which counts{" "}
                  {p.otherBudgets.length > 1 ? "their" : "its"} own POs only, not this one; both are on the vessel&apos;s Budgets sheet.
                </p>
              )}
              {p.autoCountBudget?.map((b) => <AutoCountBudgetBox key={`${b.projectNo}|${b.costItem}|${b.statusKey}|${b.year}-${b.quarter}`} b={b} onOpen={onOpen} />)}
            </div>
          </Panel>
          <PoRemarks p={p} />
        </div>
        {/* The Over budget notice is in the Value box of General. */}
        <Quarantine since={p.quarantinedSince} />
        <section className="card scroll-mt-12" id="po-positions" aria-label="Positions">
          <LineGrid
            lines={p.lines}
            cols={poLineCols(p, onOpen)}
            defaults={PO_LINE_DEFAULT}
            prefKey="po-lines"
            total={{ key: "usdValue", usd: p.usdValue }}
            cellProps={(c, l) => (c.key === "positionNo" ? feedsMark(feeds, l.positionNo) : {})}
          />
        </section>
      </WithNav>
    </div>
  );
}

/** A supplier as "no - name", opening its address-book page when it has a number. */
function SupplierLink({ no, name, onOpen }: { no: string | null; name: string | null; onOpen: (r: DrawerRef) => void }) {
  if (!no) return <>{name}</>;
  return (
    <button className="text-left text-brand hover:underline" title="Open in the address book" onClick={() => onOpen({ type: "address", no })}>
      {noName(no, name)}
    </button>
  );
}

const ADDRESS_ICON = (
  <svg {...iconProps}>
    <path d="M5 4.5A1.5 1.5 0 0 1 6.5 3H19v18H6.5A1.5 1.5 0 0 1 5 19.5z" />
    <circle cx="12" cy="10" r="2.5" />
    <path d="M8.5 16.5a3.5 3.5 0 0 1 7 0" />
  </svg>
);

/**
 * An address-book entry (SPEC §8.7), laid out as Sertica's Address Book page: the address, how it
 * buys, its contacts and categories; then the user's own POs and RFQs with it, from the store.
 */
function AddressBody({ no, onOpen, freshness }: { no: string; onOpen: (r: DrawerRef) => void; freshness?: Freshness }) {
  const qc = useQueryClient();
  const { data: a, isLoading, isError } = useRecord<AddressDetail>(recordPath("address", no));
  // Contacts have no search: an address the fill has not reached reads them on open.
  const refresh = useRefresh("address", no, () => void reloadRecords(qc), freshness, !!a && !a.contactsReadAt);
  if (isLoading) return <div className="p-4 text-muted">Loading…</div>;
  if (isError || !a)
    return <div className="p-4 text-[13px] text-muted">Address {no} is not in the address book. A new one arrives with the next sync.</div>;
  const notRead = <p className="text-[13px] text-muted">{refresh.busy ? "Reading from Sertica…" : "Not read from Sertica yet."}</p>;
  return (
    <div className="bg-hover min-h-full pb-4">
      <RecordBar icon={ADDRESS_ICON} title="Address" no={a.no} refresh={refresh} />
      <div className="p-3 space-y-3">
        <RecordHeader
          no={a.no}
          unit=""
          date={null}
          dateTitle=""
          status={a.inactive ? "Inactive" : "Active"}
          statusTitle="In Sertica's address book"
          refresh={refresh}
          fetchedAt={a.fetchedAt}
        >
          {a.name && <p className="text-[15px] text-ink">{a.name}</p>}
          {a.addressTypes && <p className="text-ink2">{spaced(a.addressTypes)}</p>}
        </RecordHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <Panel title="Address">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="Address" wide>
                {a.addressLines && <span className="whitespace-pre-line">{a.addressLines}</span>}
              </BoxField>
              <BoxField label="Zip code">{a.zipCode}</BoxField>
              <BoxField label="City">{a.city}</BoxField>
              <BoxField label="Country">{noName(a.countryCodeNo, a.countryName)}</BoxField>
              <BoxField label="Region">{a.region}</BoxField>
              <BoxField label="Phone">{a.phone}</BoxField>
              <BoxField label="Telefax">{a.telefax}</BoxField>
              <BoxField label="Mobile">{a.mobilePhone}</BoxField>
              <BoxField label="Homepage" wide>
                {a.homepage && (
                  <a className="text-brand hover:underline" href={/^https?:\/\//i.test(a.homepage) ? a.homepage : `https://${a.homepage}`} target="_blank" rel="noreferrer noopener">
                    {a.homepage}
                  </a>
                )}
              </BoxField>
            </div>
          </Panel>
          <Panel title="Purchasing">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="eProc vendor no.">{a.vendorNo}</BoxField>
              <BoxField label="Currency">{a.currencyNo}</BoxField>
              <BoxField label="RFQs sent by">{forwardType(a.rfqForwardType)}</BoxField>
              <BoxField label="POs sent by">{spaced(a.poForwardType)}</BoxField>
              {/* Sertica's own counts, over every unit. */}
              <BoxField label="RFQs (rejected)">{a.rfqsTotal != null && `${int(a.rfqsTotal)}${a.rfqsRejected ? ` (${int(a.rfqsRejected)})` : ""}`}</BoxField>
              <BoxField label="POs">{a.posTotal != null && int(a.posTotal)}</BoxField>
              <BoxField label="PO issued (USD)" wide>
                <span title="POs Ordered, Partly Received or Received in your units, at Sertica's USD value">
                  {formatUsdFull(a.issuedPoUsd)}
                  <span className="text-muted"> · {int(a.issuedPoCount)} {a.issuedPoCount === 1 ? "PO" : "POs"} in your units</span>
                </span>
              </BoxField>
              <BoxField label="Language">{a.language}</BoxField>
              <BoxField label="Framework agreement">{a.frameworkAgreement ? "Yes" : "No"}</BoxField>
              {a.classification && <BoxField label="Classification">{a.classification}</BoxField>}
              {a.expireDate && <BoxField label="Expires">{formatDate(a.expireDate)}</BoxField>}
              <BoxField label="Products" wide>
                {a.productDescription && <span className="whitespace-pre-line">{a.productDescription}</span>}
              </BoxField>
            </div>
          </Panel>
          <Panel title="Contacts">
            {!a.contactsReadAt ? (
              notRead
            ) : a.contacts.length ? (
              <ul className="divide-y divide-line text-[13px]">
                {a.contacts.map((c) => (
                  <li key={c.guid} className="py-1.5 space-y-0.5">
                    <div>
                      <span className="font-medium">{c.name ?? "—"}</span>
                      {c.title && <span className="text-ink2"> · {c.title}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-ink2">
                      {c.email && (
                        <a className="text-brand hover:underline break-all" href={`mailto:${c.email}`}>
                          {c.email}
                        </a>
                      )}
                      {c.phone && <span className="tnum">{c.phone}</span>}
                      {c.mobile && <span className="tnum">{c.mobile}</span>}
                    </div>
                    {(c.areaOfResponsibility || c.location) && <div className="text-[12px] text-muted">{[c.areaOfResponsibility, c.location].filter(Boolean).join(" · ")}</div>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-muted">None</p>
            )}
          </Panel>
          <Panel title="Categories">
            {!a.contactsReadAt ? (
              notRead
            ) : a.categories.length ? (
              <div className="flex flex-wrap gap-1.5">
                {a.categories.map((g) => (
                  <span key={g.no} className="rounded-badge border border-line px-2 py-0.5 text-[12px]" title={g.no}>
                    {g.name ?? g.no}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-muted">None</p>
            )}
          </Panel>
          {a.remarks && (
            <Panel title="Remarks">
              <p className="text-[13px] whitespace-pre-line">{a.remarks}</p>
            </Panel>
          )}
        </div>
        <AddressRecords title="Purchase orders" type="po" rows={a.pos} total={a.posCount} onOpen={onOpen} />
        <AddressRecords title="RFQs" type="rfq" rows={a.rfqs} total={a.rfqCount} onOpen={onOpen} />
      </div>
    </div>
  );
}

/** The latest POs or RFQs with an address, in the user's units; a row opens the record. */
function AddressRecords({ title, type, rows, total, onOpen }: { title: string; type: "po" | "rfq"; rows: AddressDetail["pos"]; total: number; onOpen: (r: DrawerRef) => void }) {
  return (
    <section className="card" aria-label={title}>
      <h3 className="px-3 pt-3 pb-2 text-[14px] font-medium">
        {title}
        <span className="ml-2 text-[12px] font-normal text-muted">
          {total > rows.length ? `latest ${rows.length} of ${int(total)} in your units` : `${int(total)} in your units`}
        </span>
      </h3>
      {rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <tbody>
              {rows.map((r) => (
                <tr key={r.no} className="border-t border-line hover:bg-hover cursor-pointer" onClick={() => onOpen({ type, no: r.no })}>
                  <td className="px-3 py-[5px] whitespace-nowrap">
                    <button className="text-brand hover:underline" onClick={(e) => (e.stopPropagation(), onOpen({ type, no: r.no }))}>
                      {r.no}
                    </button>
                  </td>
                  <td className="px-3 py-[5px] tnum whitespace-nowrap text-ink2">{r.date && formatDate(r.date)}</td>
                  <td className="px-3 py-[5px] text-ink2">{r.vessel ?? r.unitNo}</td>
                  <td className="px-3 py-[5px] text-ink2">{r.description}</td>
                  <td className="px-3 py-[5px] whitespace-nowrap text-ink2">{spaced(r.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-3 pb-3 text-[13px] text-muted">None</p>
      )}
    </section>
  );
}

/** A company by name, opening its page when it has a number (SPEC §8.9). */
function CompanyLink({ no, name, onOpen }: { no: string | null; name: string | null; onOpen: (r: DrawerRef) => void }) {
  if (!no) return <>{name}</>;
  return (
    <button className="text-left text-brand hover:underline" title="Open the company" onClick={() => onOpen({ type: "company", no })}>
      {name ?? no}
    </button>
  );
}

const COMPANY_ICON = (
  <svg {...iconProps}>
    <rect x="4" y="3" width="16" height="18" rx="1" />
    <path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2M10 21v-3h4v3" />
  </svg>
);

/**
 * A company (SPEC §8.9), laid out as Sertica's Company page: its address and approval rules, the
 * roles that approve its POs and the approval setups they follow; then the user's own POs under it.
 */
function CompanyBody({ no, onOpen, freshness }: { no: string; onOpen: (r: DrawerRef) => void; freshness?: Freshness }) {
  const qc = useQueryClient();
  const { data: c, isLoading, isError } = useRecord<CompanyDetail>(recordPath("company", no));
  // Approvers have no search: a company not read yet reads them on open.
  const refresh = useRefresh("company", no, () => void reloadRecords(qc), freshness, !!c && !c.approversReadAt);
  if (isLoading) return <div className="p-4 text-muted">Loading…</div>;
  if (isError || !c) return <div className="p-4 text-[13px] text-muted">Company {no} is not in the store. A new one arrives with the next sync.</div>;
  const notRead = <p className="text-[13px] text-muted">{refresh.busy ? "Reading from Sertica…" : "Not read from Sertica yet."}</p>;
  const yes = (b: boolean) => (b ? "Yes" : "No");
  return (
    <div className="bg-hover min-h-full pb-4">
      <RecordBar icon={COMPANY_ICON} title="Company" no={c.no} refresh={refresh} />
      <div className="p-3 space-y-3">
        <RecordHeader no={c.no} unit="" date={null} dateTitle="" status={c.approvalRequired ? "Approval required" : "No approval"} statusTitle="Whether its POs need approval" refresh={refresh} fetchedAt={c.fetchedAt}>
          {c.name && <p className="text-[15px] text-ink">{c.name}</p>}
        </RecordHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <Panel title="General">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="Address" wide>
                {c.addressLine && <span className="whitespace-pre-line">{c.addressLine}</span>}
              </BoxField>
              <BoxField label="Zip code">{c.zipCode}</BoxField>
              <BoxField label="City">{c.city}</BoxField>
              <BoxField label="Country">{noName(c.countryCodeNo, c.countryName)}</BoxField>
              <BoxField label="Phone">{c.phone}</BoxField>
              <BoxField label="Telefax">{c.telefax}</BoxField>
              <BoxField label="Email">{c.email}</BoxField>
            </div>
          </Panel>
          <Panel title="Approval">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="Approval required">{yes(c.approvalRequired)}</BoxField>
              <BoxField label="Approval type">{spaced(c.approvalType)}</BoxField>
              <BoxField label="Approval limit (USD)">{c.approvalLimit != null && formatUsdFull(c.approvalLimit)}</BoxField>
              <BoxField label="PO must be priced">{yes(c.poMustBePriced)}</BoxField>
              <BoxField label="Requisition must be priced">{yes(c.requisitionMustBePriced)}</BoxField>
              <BoxField label="Purchaser and approver differ">{yes(c.purchaserAndApproverDifferent)}</BoxField>
              <BoxField label="PO issued (USD)" wide>
                <span title="POs Ordered, Partly Received or Received in your units, at Sertica's USD value">
                  {formatUsdFull(c.issuedPoUsd)}
                  <span className="text-muted"> · {int(c.issuedPoCount)} {c.issuedPoCount === 1 ? "PO" : "POs"} in your units</span>
                </span>
              </BoxField>
            </div>
          </Panel>
        </div>
        <section className="card" aria-label="Approvers">
          <h3 className="px-3 pt-3 pb-2 text-[14px] font-medium">Approvers</h3>
          {!c.approversReadAt ? (
            <div className="px-3 pb-3">{notRead}</div>
          ) : c.approvers.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="px-3 py-1 font-normal">Role</th>
                    <th className="px-3 py-1 font-normal">Type</th>
                    <th className="px-3 py-1 font-normal text-right">Approval limit (USD)</th>
                    <th className="px-3 py-1 font-normal text-right">Single approval limit (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {c.approvers.map((a) => (
                    <tr key={a.guid} className="border-t border-line">
                      <td className="px-3 py-[5px]">{noName(a.roleNo, a.roleName)}</td>
                      <td className="px-3 py-[5px] text-ink2">{spaced(a.type)}</td>
                      <td className="px-3 py-[5px] tnum text-right">{a.approvalLimit != null && formatUsdFull(a.approvalLimit)}</td>
                      <td className="px-3 py-[5px] tnum text-right">{a.singleApprovalLimit != null && formatUsdFull(a.singleApprovalLimit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-3 pb-3 text-[13px] text-muted">None</p>
          )}
        </section>
        <section className="card" aria-label="Approval setups">
          <h3 className="px-3 pt-3 pb-2 text-[14px] font-medium">Approval setups</h3>
          {!c.approversReadAt ? (
            <div className="px-3 pb-3">{notRead}</div>
          ) : c.approvalSetups.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="px-3 py-1 font-normal">Vessel</th>
                    <th className="px-3 py-1 font-normal">Approval setup</th>
                    <th className="px-3 py-1 font-normal">Purchase type</th>
                    <th className="px-3 py-1 font-normal">Technical account</th>
                    <th className="px-3 py-1 font-normal">Department</th>
                    <th className="px-3 py-1 font-normal">Project</th>
                  </tr>
                </thead>
                <tbody>
                  {c.approvalSetups.map((s) => (
                    <tr key={s.guid} className="border-t border-line text-ink2">
                      <td className="px-3 py-[5px] text-ink">{s.unitName ?? s.unitNo}</td>
                      <td className="px-3 py-[5px]">{noName(s.approvalSetupNo, s.approvalSetupName)}</td>
                      <td className="px-3 py-[5px]">{noName(s.purchaseTypeNo, s.purchaseTypeName)}</td>
                      <td className="px-3 py-[5px]">{noName(s.technicalAccountNo, s.technicalAccountName)}</td>
                      <td className="px-3 py-[5px]">{noName(s.departmentNo, s.departmentName)}</td>
                      <td className="px-3 py-[5px]">{noName(s.projectNo, s.projectName)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-3 pb-3 text-[13px] text-muted">None</p>
          )}
        </section>
        <AddressRecords title="Purchase orders" type="po" rows={c.pos} total={c.posCount} onOpen={onOpen} />
      </div>
    </div>
  );
}

/** Sertica's name for how an RFQ went out: Email sends the RFQ as a PDF. */
const forwardType = (v: string | null) => (v === "Email" ? "Email (PDF)" : spaced(v));

/**
 * An RFQ's header and everything linked through its requisitions (SPEC §8.2a), laid out as the
 * requisition's page; its quoted lines are read with its refresh.
 */
function RfqBody({ no, onOpen, freshness }: { no: string; onOpen: (r: DrawerRef) => void; freshness?: Freshness }) {
  const qc = useQueryClient();
  const { data: q, isLoading, isError, error } = useRecord<RfqDetail>(recordPath("rfq", no));
  // An RFQ's refresh reads its whole round, with the quotes (SPEC §11.3); on open, as for the other records.
  const refresh = useRefresh("rfq", no, () => void reloadRecords(qc), freshness, !!q && (!q.usersRead || fetchOnOpen(q.fetchedAt, CLOSED_RFQ.has(q.status), freshness)));
  if (isLoading) return <div className="p-4 text-muted">Loading…</div>;
  if (isError || !q) return <NotInStore type="rfq" no={no} error={error} />;
  const sec = (s: string) => `rfq-${s}`;
  const jump = (s: string) => document.getElementById(sec(s))?.scrollIntoView({ behavior: "smooth" });
  const lines = q.lines ?? [];
  // The lines' own sum: the header total also holds the delivery costs.
  const sumUsd = lines.length && lines.every((l) => l.totalUsd != null) ? lines.reduce((s, l) => s + l.totalUsd!, 0) : null;
  return (
    <div className="bg-hover min-h-full pb-4">
      <RecordBar icon={RFQ_ICON} title="Request for quote" no={q.no} refresh={refresh}>
        <button onClick={() => onOpen({ type: "rfq-compare", no: q.no })}>
          <svg aria-hidden viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="3" width="14" height="18" rx="1.5" />
            <path d="M8 7h8M8 11h2M12 11h2M8 15h2M12 15h2M16 11v4" />
          </svg>
          Calculate PO
        </button>
        <button onClick={() => jump("nav")}>Show connected RFQs</button>
      </RecordBar>
      <WithNav
        nav={
          <div id={sec("nav")} className="scroll-mt-12">
            <NavTree kind="rfq" record={q} onOpen={onOpen} />
          </div>
        }
      >
        {/* Each fact once: number, unit, requested delivery date and Sertica's status on top; the panels below hold the rest. */}
        <RecordHeader
          no={q.no}
          unit={noName(q.unitNo, q.vessel)}
          date={q.requestedDeliveryDate}
          dateTitle="Requested delivery date"
          status={[spaced(q.status), spaced(q.workflow)].filter(Boolean).join(" - ")}
          statusTitle="Status - Workflow in Sertica"
          refresh={refresh}
          fetchedAt={q.fetchedAt}
        >
          {q.description && <p className="text-[15px] text-ink">{q.description}</p>}
          {(q.supplierNo || q.supplier) && (
            <p className="text-ink2">
              <SupplierLink no={q.supplierNo} name={q.supplier} onOpen={onOpen} />
            </p>
          )}
          <p className="text-[11.5px] text-muted pt-0.5">Calculate PO compares the quotes of every supplier this RFQ went to. Choosing the supplier and creating the PO stay in Sertica.</p>
        </RecordHeader>
        <OverBudgetBlock o={q.overBudget} freshness={freshness} onOpen={onOpen} />
        <div className="grid md:grid-cols-2 gap-3">
          <Panel title="General">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="Currency">{q.currency}</BoxField>
              <BoxField label="Forward type">{forwardType(q.sentVia)}</BoxField>
              <BoxField label="Total">{q.totalPrice != null && q.currency && formatOrderAmount(q.totalPrice, q.currency)}</BoxField>
              <BoxField label="Total (USD)">{q.totalUsd != null && formatUsdFull(q.totalUsd)}</BoxField>
              <BoxField label="Discount">{q.discount != null && `${q.discount}%`}</BoxField>
            </div>
          </Panel>
          {/* Sertica's Supplier info. tab; its never-filled fields (agent, conditions, valid until…) left out. */}
          <Panel title="Supplier info">
            <div className="grid grid-cols-2 gap-2">
              <BoxField label="Remark from supplier" wide>
                {q.remarkFromSupplier && <span className="whitespace-pre-line">{q.remarkFromSupplier}</span>}
              </BoxField>
              <BoxField label="Sent to" wide>
                {q.sentTo && <span className="break-all">{q.sentTo}</span>}
              </BoxField>
            </div>
          </Panel>
          <div className="md:col-span-2">
            <Panel title="Send to supplier and responses">
              <RfqPeople p={q} />
            </Panel>
          </div>
        </div>
        <section className="card scroll-mt-12" id={sec("positions")} aria-label="Positions">
          {q.lines === null ? (
            <>
              <h3 className="px-4 pt-3 pb-2 text-[17px] text-ink">Positions</h3>
              <p className="px-4 pb-3 text-[13px] text-muted">Not read from Sertica yet; opening the RFQ reads its lines.</p>
            </>
          ) : (
            <LineGrid lines={lines} cols={rfqLineCols(q, onOpen)} defaults={RFQ_LINE_DEFAULT} prefKey="rfq-lines" total={sumUsd != null ? { key: "totalUsd", usd: sumUsd } : undefined} />
          )}
        </section>
      </WithNav>
    </div>
  );
}

type RfqLine = NonNullable<RfqDetail["lines"]>[number];

const qtyOr = (n: number | null) => (n != null ? qtyFmt.format(n) : "—");
const inCurrency = (n: number | null, currency: string | null) => (n == null ? "—" : currency ? formatOrderAmount(n, currency) : qtyFmt.format(n));

/** Every column the RFQ's position grid can show, as Sertica's quote grid; the first ten are the default. */
const rfqLineCols = (q: RfqDetail, onOpen: (r: DrawerRef) => void): GridCol<RfqLine>[] => [
  { key: "positionNo", label: "Position no.", right: true, value: (l) => l.positionNo, cell: (l) => l.positionNo },
  { key: "itemNo", label: "Item no.", value: (l) => l.itemNo, cell: (l) => l.itemNo ?? "—" },
  {
    key: "name",
    label: "Position name",
    value: (l) => l.name,
    cell: (l) => (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <button className="text-left text-brand hover:underline" onClick={() => onOpen({ type: "position", recordType: "rfq", recordNo: q.no, positionNo: l.positionNo })}>
          {l.name ?? "—"}
        </button>
        {l.critical && <CriticalChip remark={l.criticalRemark} />}
      </span>
    ),
  },
  { key: "requestedQuantity", label: "Requested quantity", right: true, value: (l) => l.requestedQuantity, cell: (l) => qtyOr(l.requestedQuantity) },
  { key: "quotedQuantity", label: "Quoted quantity", right: true, value: (l) => l.quotedQuantity, cell: (l) => qtyOr(l.quotedQuantity) },
  { key: "unit", label: "Measuring unit", value: (l) => l.unit, cell: (l) => l.unit ?? "—" },
  { key: "price", label: "Price", right: true, value: (l) => l.price, cell: (l) => inCurrency(l.price, q.currency) },
  { key: "discount", label: "Discount (%)", right: true, value: (l) => l.discount, cell: (l) => (l.discount ? qtyFmt.format(l.discount) : "—") },
  { key: "totalPrice", label: "Total price", right: true, value: (l) => l.totalPrice, cell: (l) => inCurrency(l.totalPrice, q.currency) },
  { key: "totalUsd", label: "Total price (USD)", right: true, value: (l) => l.totalUsd, cell: (l) => (l.totalUsd != null ? formatUsdFull(l.totalUsd) : "—") },
  // Optional.
  { key: "quantityToOrder", label: "Quantity to order", right: true, value: (l) => l.quantityToOrder, cell: (l) => qtyOr(l.quantityToOrder) },
  { key: "remark", label: "Remark", wrap: true, value: (l) => l.details?.remark, cell: (l) => rfqDetail(l, "remark") },
  { key: "internalRemark", label: "Internal remark", wrap: true, value: (l) => l.details?.internalRemark, cell: (l) => rfqDetail(l, "internalRemark") },
  { key: "workflow", label: "Workflow", value: (l) => l.workflow, cell: (l) => spaced(l.workflow) ?? "—" },
  { key: "supplierItemNo", label: "Supplier item no.", value: (l) => l.supplierItemNo, cell: (l) => l.supplierItemNo ?? "—" },
  { key: "onPo", label: "On PO", value: (l) => l.onPo, cell: (l) => (l.onPo ? "Yes" : "No") },
  { key: "priceTrend", label: "Price trend", value: (l) => l.priceTrend, cell: (l) => (l.priceTrend ? <Trend t={l.priceTrend} /> : "—") },
  ...(
    [
      ["itemName", "Item name"],
      ["referenceNo", "Reference no."],
      ["referencePosNo", "Reference pos. no."],
      ["itemReferenceNo", "Item reference no."],
      ["manufacture", "Manufacturer"],
      ["typeNo", "Type no."],
      ["classification", "Classification"],
      ["drawingNo", "Drawing no."],
      ["drawingPosNo", "Drawing pos. no."],
      ["standardNo", "Standard no."],
      ["dimensions", "Dimensions"],
      ["measureUnitNo", "Measuring unit no."],
    ] as const
  ).map(([key, label]): GridCol<RfqLine> => ({ key, label, value: (l) => l.details?.[key], cell: (l) => rfqDetail(l, key) })),
  {
    key: "totalQuantityFromRequisitions",
    label: "Quantity from requisitions",
    right: true,
    value: (l) => l.details?.totalQuantityFromRequisitions,
    cell: (l) => {
      const q = l.details?.totalQuantityFromRequisitions;
      return typeof q === "number" ? `${qtyFmt.format(q)}${l.details?.measuringUnitNoFromRequisition ? ` ${l.details.measuringUnitNoFromRequisition}` : ""}` : "—";
    },
  },
];
/** One of Sertica's other line fields; "—" when empty or not read yet. */
const rfqDetail = (l: RfqLine, key: string) => {
  const v = l.details?.[key];
  return v == null || v === "" ? "—" : String(v);
};
const RFQ_LINE_DEFAULT = ["positionNo", "itemNo", "name", "requestedQuantity", "quotedQuantity", "unit", "price", "discount", "totalPrice", "totalUsd"];

type People = Pick<
  RfqDetail,
  "sentBy" | "sentByName" | "sentVia" | "dateSent" | "createdBy" | "createdByName" | "createDate" | "receivedVia" | "quoteEnteredAt" | "updatedBy" | "updatedByName" | "updateDate" | "externalReference"
>;

/** A Sertica user by number, the name on hover (SPEC §9); the name alone while the number is not synced yet. */
function UserNo({ no, name }: { no: string | null; name?: string | null }) {
  // Always the number (SPEC §8.2a); before the refill has read it, a dash with the name on hover.
  if (!no)
    return (
      <span className="text-muted" title={name ? `${name} (user number not read from Sertica yet)` : undefined}>
        —
      </span>
    );
  return (
    <span className="tnum" title={name ?? undefined}>
      {no}
    </span>
  );
}

/**
 * Who sent the RFQ and how its quote came in (SPEC §8.2a). Sertica records no "quote entered by":
 * a quote with no channel was typed in by a buyer, and the last updater is the best clue to who.
 */
function RfqPeople({ p }: { p: People }) {
  const at = (d: string | null) => (d ? ` · ${formatDate(d)}` : "");
  return (
    <div className="grid grid-cols-2 gap-2">
      <BoxField label="Sent by">
        {p.sentBy || p.sentByName ? (
          <>
            <UserNo no={p.sentBy} name={p.sentByName} />
            {p.sentVia ? ` by ${p.sentVia}` : ""}
            {at(p.dateSent)}
          </>
        ) : p.dateSent ? (
          `—${at(p.dateSent)}`
        ) : (
          "Not sent"
        )}
      </BoxField>
      <BoxField label="Created by">
        {(p.createdBy || p.createdByName) && (
          <>
            <UserNo no={p.createdBy} name={p.createdByName} />
            {at(p.createDate)}
          </>
        )}
      </BoxField>
      <BoxField label="Quote entered">
        {p.quoteEnteredAt ? `${formatDate(p.quoteEnteredAt)} · ${p.receivedVia ? `by the supplier via ${p.receivedVia}` : "typed in by a buyer"}` : "No quote yet"}
      </BoxField>
      <BoxField label="Last updated by">
        {p.updatedBy && (
          <>
            <span className="tnum" title={p.updatedByName ?? undefined}>
              {p.updatedBy}
            </span>
            {at(p.updateDate)}
          </>
        )}
      </BoxField>
      <BoxField label="External reference">{p.externalReference}</BoxField>
    </div>
  );
}

/** A Sertica summary box under a comparison panel: a small label over a figure. */
const SumBox = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
  <div className="rounded-input border border-line bg-card px-2 pt-1 pb-1.5 w-[150px]">
    <div className="text-[10.5px] text-muted truncate">{label}</div>
    <div className="text-[13px] tnum text-right whitespace-nowrap">{children}</div>
  </div>
);

/** Green for the cheapest, red for the dearest, as Sertica colours a comparison. */
const BEST = "text-[var(--stage-received)]";
const WORST = "text-[var(--danger)]";
const CO2 = (
  <>
    CO<sub>2</sub>
  </>
);
/** An amount as Sertica prints it: "5,465.00 USD". */
const amt = (v: number | null | undefined, currency: string | null | undefined) => {
  if (v == null || !currency) return "";
  const [code, ...rest] = formatOrderAmount(v, currency).split(" ");
  return `${rest.join(" ")} ${code}`;
};
const usdAmt = (v: number | null | undefined) => amt(v, "USD");
const grams = (v: number | null | undefined) => (v != null ? `${int(v)} g` : "g");
const minOf = (vs: (number | null | undefined)[]) => {
  const xs = vs.filter((v): v is number => v != null);
  return xs.length ? Math.min(...xs) : null;
};
const sumOf = (vs: (number | null | undefined)[]) => {
  const xs = vs.filter((v): v is number => v != null);
  return xs.length ? xs.reduce((a, b) => a + b, 0) : null;
};
const dateOr = (d: string | null | undefined) => (d ? formatDate(d) : "");

/** Sertica's "+" that opens a row's detail. */
const Expander = ({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) => (
  <button className="w-5 h-5 inline-flex items-center justify-center text-ink2 hover:text-ink text-[15px] leading-none" aria-expanded={open} aria-label={label} onClick={onClick}>
    {open ? "−" : "+"}
  </button>
);

/** How the quote came in, as Sertica's icon column: a pencil when a buyer typed it in, else the channel. */
const QuoteSource = ({ via }: { via: string | null }) =>
  via ? (
    <span title={`Quote received via ${via}`} className="text-[var(--danger)]" aria-label={`Received via ${via}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M14.8 9.2A3 3 0 0 0 12 8c-1.7 0-3 .9-3 2s1.3 1.7 3 2 3 .9 3 2-1.3 2-3 2a3 3 0 0 1-2.8-1.2" />
      </svg>
    </span>
  ) : (
    <span title="Quote entered by a buyer" className="text-[var(--stage-received)]" aria-label="Entered by a buyer">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 20l4-1 11-11-3-3L5 16l-1 4z" />
      </svg>
    </span>
  );

/** `priceTrendIndicator`: the price against the supplier's last one. */
const Trend = ({ t }: { t: string | null }) =>
  t === "Increasing" || t === "Decreasing" || t === "Unchanged" ? (
    <span title={`Price ${t.toLowerCase()} against the last order`} aria-label={`Price ${t.toLowerCase()}`} className="text-ink2">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {t === "Increasing" ? <path d="M3 17l6-6 4 4 8-8M15 7h6v6" /> : t === "Decreasing" ? <path d="M3 7l6 6 4-4 8 8M15 17h6v-6" /> : <path d="M3 12h16M15 8l4 4-4 4" />}
      </svg>
    </span>
  ) : null;

/** A collapsible Sertica panel: title, chevron, body. */
function CalcSection({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="card overflow-hidden" aria-label={title}>
      <button className="w-full flex items-center justify-between px-4 pt-3 pb-2 text-left" aria-expanded={open} onClick={() => setOpen(!open)}>
        <h3 className="text-[16px] tracking-[0.01em] text-ink">{title}</h3>
        <span aria-hidden className={`text-muted transition-transform ${open ? "" : "rotate-180"}`}>
          ⌃
        </span>
      </button>
      {open && children}
    </section>
  );
}

/**
 * Sertica's footer under a comparison: its figures, then its order button (disabled: ordering stays
 * in Sertica). Delivery and CO₂ show only when a quote in the round has them: Sertica fills neither
 * on RFQ lines today (research/rfq-positions/report.json).
 */
function CalcFooter(p: {
  fastest: string | null;
  deliverySel: string | null;
  lowestCo2: number | null;
  co2Sel: number | null;
  best: number | null;
  priceSel: number | null;
  button: string;
  has: { delivery: boolean; co2: boolean };
}) {
  return (
    <div className="flex flex-wrap justify-end items-end gap-2 px-3 py-2 border-t border-line">
      {p.has.delivery && (
        <>
          <SumBox label="Fastest Delivery">{dateOr(p.fastest)}</SumBox>
          <SumBox label="Delivery Date (Selected)">{dateOr(p.deliverySel)}</SumBox>
        </>
      )}
      {p.has.co2 && (
        <>
          <SumBox label={<>Lowest possible {CO2}</>}>{grams(p.lowestCo2)}</SumBox>
          <SumBox label={<>Total {CO2} (Selected)</>}>{grams(p.co2Sel)}</SumBox>
        </>
      )}
      <SumBox label="Best Possible Price">{usdAmt(p.best)}</SumBox>
      <SumBox label="Total Price (Selected)">{usdAmt(p.priceSel)}</SumBox>
      <button disabled className="h-9 px-3 rounded-input text-[13px] bg-[var(--brand)] text-white opacity-50 cursor-not-allowed" title="Read-only: create the order in Sertica">
        {p.button}
      </button>
    </div>
  );
}

/**
 * Calculate PO (SPEC §8.2a), laid out as Sertica's Calculate Purchase Order: the suppliers' whole
 * quotes first, then each position across the suppliers. "(std.)" is Sertica's standard currency,
 * USD. The radio and ticks start on what went onto a live PO (else the cheapest) and can be changed
 * to compare; nothing is saved and no order is created.
 */
function RfqCompareBody({ no, onOpen, freshness }: { no: string; onOpen: (r: DrawerRef) => void; freshness?: Freshness }) {
  const qc = useQueryClient();
  const { data: c, isLoading, isError, error } = useRecord<RfqComparison>(`/rfqs/${encodeURIComponent(no)}/comparison`);
  const fetchedSome = !!c?.suppliers.some((s) => s.fetchedAt);
  const refresh = useRefresh("rfq", no, () => void qc.invalidateQueries({ queryKey: ["record"] }), freshness, !!c && !fetchedSome);
  if (isLoading) return <div className="p-4 text-muted">Loading…</div>;
  if (isError || !c) return <NotInStore type="rfq" no={no} error={error} freshness={freshness} />;
  const fetched = c.suppliers.map((s) => s.fetchedAt).filter((x): x is string => !!x).sort();
  return (
    <div className="bg-hover min-h-full pb-4">
      <div className="flex items-center gap-2 px-4 h-11 bg-card">
        <span className="text-brand-2" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="3" width="16" height="18" rx="1.5" />
            <path d="M8 8h8M8 12h8M8 16h5" />
          </svg>
        </span>
        <span className="text-[14px] font-bold uppercase tracking-[0.02em]">Calculate purchase order</span>
        <span className="text-[13px] text-muted tnum">
          RFQ {c.no} · {c.roundSize} {c.roundSize === 1 ? "supplier" : "suppliers"}
        </span>
      </div>
      <div className="toolbar flex items-center gap-1 px-3 h-10">
        <button disabled={refresh.busy} onClick={refresh.start}>
          <svg aria-hidden viewBox="0 0 24 24" className={`w-4 h-4 ${refresh.busy ? "animate-spin" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v6h-6" />
          </svg>
          {refresh.busy ? "Fetching from Sertica…" : "Fetch quotes again"}
        </button>
      </div>
      <div className="p-3 space-y-3">
        <div className="space-y-1.5">
          <RefreshStatus r={refresh} />
          <p className="text-[11.5px] text-muted">
            {fetched.length ? `Quotes fetched from Sertica at ${formatWib(new Date(fetched[0]!))}. ` : "Quotes are read from Sertica when you open this. "}
            (std.) is USD at each quote&apos;s own exchange rate. Green is the cheapest, red the dearest. Suppliers with a PO are highlighted. The selection starts on what went onto a live PO and can be changed to compare; nothing is saved. Ordering stays in Sertica.
          </p>
        </div>
        <CalcPo key={c.suppliers.map((s) => `${s.rfqNo}:${s.fetchedAt}`).join()} c={c} onOpen={onOpen} />
      </div>
    </div>
  );
}

type Supplier = RfqComparison["suppliers"][number];

/** The highlight of a supplier that got a live PO from the round. */
const AWARDED = "bg-[color-mix(in_srgb,var(--brand)_12%,transparent)]";

/** A supplier's POs from the round, each opening its drawer; a cancelled one struck through in red. */
function PoNos({ s, onOpen }: { s: Supplier; onOpen: (r: DrawerRef) => void }) {
  // Before the PO links are read, status Ordered alone says a PO exists.
  if (!s.pos.length) return s.poIssued ? <span className="text-muted">PO issued</span> : null;
  return (
    <>
      {s.pos.map((p, i) => (
        <span key={p.no}>
          {i > 0 && ", "}
          <button
            className={`font-semibold hover:underline ${p.stage === "cancelled" ? "text-[var(--danger)] line-through" : "text-brand"}`}
            title={p.stage === "cancelled" ? "PO cancelled" : "Open the PO"}
            onClick={(e) => (e.stopPropagation(), onOpen({ type: "po", no: p.no }))}
          >
            {p.no}
          </button>
        </span>
      ))}
    </>
  );
}
type Item = RfqComparison["items"][number];

function CalcPo({ c, onOpen }: { c: RfqComparison; onOpen: (r: DrawerRef) => void }) {
  // Whole quotes: complete ones only compete, as a partial quote looks cheaper than it is.
  const complete = c.suppliers.filter((s) => c.items.length > 0 && s.quotedLines === c.items.length && (s.totalUsd ?? 0) > 0);
  const dearest = complete.length > 1 ? complete.reduce((a, b) => (b.totalUsd! > a.totalUsd! ? b : a)).rfqNo : null;
  const tone = (rfqNo: string) => (rfqNo === c.cheapest ? BEST : rfqNo === dearest ? WORST : "");
  const priced = (it: Item, rfqNo: string) => (it.quotes[rfqNo]?.priceUsd ?? 0) > 0;
  const itemDearest = (it: Item) => {
    const ps = Object.entries(it.quotes).filter(([, q]) => (q.priceUsd ?? 0) > 0);
    return ps.length > 1 ? ps.reduce((a, b) => (b[1].priceUsd! > a[1].priceUsd! ? b : a))[0] : null;
  };

  const [single, setSingle] = useState<string | null>(() => c.suppliers.find((s) => s.poIssued)?.rfqNo ?? c.cheapest ?? complete[0]?.rfqNo ?? null);
  const [picks, setPicks] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(c.items.map((it) => [it.key, it.awardedTo[0] ?? it.cheapest ?? null])),
  );
  const [openSup, setOpenSup] = useState<Set<string>>(new Set());
  const [openItem, setOpenItem] = useState<Set<string>>(new Set());
  const toggle = (set: Set<string>, k: string) => {
    const n = new Set(set);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    return n;
  };

  // Delivery dates and CO₂ only when some quote in the round has them.
  const quotes = c.items.flatMap((it) => Object.values(it.quotes));
  const has = {
    delivery: c.suppliers.some((s) => s.deliveryDate) || quotes.some((q) => q?.deliveryDate),
    co2: c.suppliers.some((s) => s.co2 != null) || quotes.some((q) => q?.co2 != null),
  };
  // Footer, whole quotes.
  const sel = c.suppliers.find((s) => s.rfqNo === single);
  const top = {
    fastest: c.suppliers.map((s) => s.deliveryDate).filter((d): d is string => !!d).sort()[0] ?? null,
    deliverySel: sel?.deliveryDate ?? null,
    lowestCo2: minOf(c.suppliers.map((s) => s.co2)),
    co2Sel: sel?.co2 ?? null,
    best: minOf(complete.map((s) => s.totalUsd)),
    priceSel: sel?.totalUsd ?? null,
  };
  // Footer, positions: each line from its cheapest quote, beside the ticked ones.
  const cellsOf = (it: Item) => Object.values(it.quotes).filter((q) => (q.priceUsd ?? 0) > 0);
  const chosen = c.items.flatMap((it) => {
    const r = picks[it.key];
    return r && it.quotes[r] ? [it.quotes[r]!] : [];
  });
  const lineDates = c.items.flatMap((it) => cellsOf(it).map((q) => q.deliveryDate)).filter((d): d is string => !!d).sort();
  const pos = {
    fastest: lineDates[0] ?? null,
    deliverySel: chosen.map((q) => q.deliveryDate).filter((d): d is string => !!d).sort().at(-1) ?? null,
    lowestCo2: sumOf(c.items.map((it) => minOf(cellsOf(it).map((q) => q.co2)))),
    co2Sel: sumOf(chosen.map((q) => q.co2)),
    best: sumOf(c.items.map((it) => minOf(cellsOf(it).map((q) => q.totalUsd)))),
    priceSel: sumOf(chosen.map((q) => q.totalUsd)),
  };

  const th = "col px-2 py-2 text-left whitespace-nowrap font-semibold text-ink";
  const td = "px-2 py-[5px] whitespace-nowrap";
  const supName = (s: Supplier) => [s.supplierNo, s.supplier].filter(Boolean).join(" - ") || s.rfqNo;
  return (
    <>
      <CalcSection title="Order All Positions From A Single Supplier">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px] tnum">
            <thead>
              <tr className="border-b border-line">
                <th className={`${th} pl-3 w-6`}>
                  <span className="sr-only">Details</span>
                </th>
                <th className={`${th} w-6`}>
                  <span className="sr-only">Select</span>
                </th>
                <th className={`${th} w-6`}>
                  <span className="sr-only">Quote source</span>
                </th>
                <th className={th}>Sup. No</th>
                <th className={th}>Supplier Name</th>
                <th className={th}>PO No.</th>
                <th className={th}>Del. Place No.</th>
                <th className={th}>Sup. Country</th>
                {has.co2 && <th className={`${th} text-right`}>Total {CO2}</th>}
                {has.delivery && <th className={`${th} text-right`}>Del. Date</th>}
                <th className={`${th} text-right`}>Del. costs</th>
                <th className={`${th} text-right`}>Discount</th>
                <th className={`${th} text-right`}>Total</th>
                <th className={`${th} text-right`}>Del. costs (std.)</th>
                <th className={`${th} text-right pr-4`}>Total (std.)</th>
              </tr>
            </thead>
            <tbody>
              {c.suppliers.map((s) => {
                const open = openSup.has(s.rfqNo);
                return (
                  <Fragment key={s.rfqNo}>
                    <tr className={`border-b border-line ${tone(s.rfqNo)} ${s.poIssued ? `${AWARDED} font-semibold` : single === s.rfqNo ? "bg-hover" : ""}`}>
                      <td className={`${td} pl-3`}>
                        <Expander open={open} label={`Details of ${supName(s)}`} onClick={() => setOpenSup(toggle(openSup, s.rfqNo))} />
                      </td>
                      <td className={td}>
                        <input type="radio" name={`calc-${c.no}`} checked={single === s.rfqNo} onChange={() => setSingle(s.rfqNo)} aria-label={`Compare ordering all positions from ${supName(s)}`} className="align-middle" />
                      </td>
                      <td className={td}>
                        <QuoteSource via={s.receivedVia} />
                      </td>
                      <td className={td}>{s.supplierNo}</td>
                      <td className={`${td} min-w-[180px]`}>{s.supplier}</td>
                      <td className={`${td} font-normal`}>
                        <PoNos s={s} onOpen={onOpen} />
                      </td>
                      <td className={td}>{s.deliveryPlaceNo}</td>
                      <td className={td}>{s.country}</td>
                      {has.co2 && <td className={`${td} text-right`}>{s.co2 != null ? grams(s.co2) : ""}</td>}
                      {has.delivery && <td className={`${td} text-right`}>{dateOr(s.deliveryDate)}</td>}
                      <td className={`${td} text-right`}>{amt(s.deliveryCosts ?? (s.fetchedAt ? 0 : null), s.currency)}</td>
                      <td className={`${td} text-right`}>{s.fetchedAt || s.discount != null ? `${(s.discount ?? 0).toFixed(2)} %` : ""}</td>
                      <td className={`${td} text-right`}>{amt(s.totalPrice, s.currency)}</td>
                      <td className={`${td} text-right`}>{usdAmt(s.deliveryCostsUsd ?? (s.fetchedAt ? 0 : null))}</td>
                      <td className={`${td} text-right pr-4`}>{usdAmt(s.totalUsd)}</td>
                    </tr>
                    {open && (
                      <tr className="border-b border-line bg-hover">
                        <td />
                        <td colSpan={12 + Number(has.co2) + Number(has.delivery)} className="px-2 py-2">
                          <div className="flex flex-wrap gap-x-8 gap-y-1 text-[12px] text-ink2">
                            <span>
                              RFQ{" "}
                              <button className="text-brand hover:underline" onClick={() => onOpen({ type: "rfq", no: s.rfqNo })}>
                                {s.rfqNo}
                              </button>{" "}
                              · {spaced(s.status) ?? "—"}
                            </span>
                            <span>Items quoted: {s.fetchedAt ? `${s.quotedLines} of ${c.items.length}` : "not fetched"}</span>
                            <span>
                              Sent by <UserNo no={s.sentBy} name={s.sentByName} />
                              {s.dateSent && ` · ${formatDate(s.dateSent)}`}
                            </span>
                            <span>Quote entered: {s.quoteEnteredAt ? `${formatDate(s.quoteEnteredAt)} ${s.receivedVia ? `via ${s.receivedVia}` : "by a buyer"}` : "—"}</span>
                            <span>
                              Last updated by{" "}
                              <span className="tnum" title={s.updatedByName ?? undefined}>
                                {s.updatedBy ?? "—"}
                              </span>
                            </span>
                            {s.pos.length > 0 && (
                              <span>
                                {s.pos.map((p, i) => (
                                  <span key={p.no}>
                                    {i > 0 && ", "}
                                    <button className={`hover:underline ${p.stage === "cancelled" ? "text-[var(--danger)] line-through" : "text-brand"}`} onClick={() => onOpen({ type: "po", no: p.no })}>
                                      PO {p.no}
                                    </button>
                                  </span>
                                ))}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="min-h-[24px]" />
        <CalcFooter {...top} has={has} button="View Order" />
      </CalcSection>

      <CalcSection title="Order Positions From Selected Suppliers">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px] tnum border-collapse">
            <thead>
              <tr>
                <th colSpan={6} className="sticky left-0 bg-card" />
                {c.suppliers.map((s) => (
                  <th key={s.rfqNo} colSpan={5} className={`px-2 pt-1 pb-1 text-center text-ink border-l border-line whitespace-nowrap ${s.poIssued ? `${AWARDED} font-semibold` : "font-normal"}`}>
                    <span className="inline-flex items-center gap-1.5">
                      <QuoteSource via={s.receivedVia} />
                      {supName(s)}
                    </span>
                    {(s.poIssued || s.pos.length > 0) && (
                      <div className="text-[11.5px] font-normal">
                        PO <PoNos s={s} onOpen={onOpen} />
                      </div>
                    )}
                  </th>
                ))}
              </tr>
              <tr className="border-b border-line">
                <th className={`${th} pl-3 w-6 sticky left-0 bg-card`}>
                  <span className="sr-only">Details</span>
                </th>
                <th className={`${th} text-right`}>Pos.</th>
                <th className={`${th} min-w-[200px]`}>Position Name</th>
                <th className={th}>Part No.</th>
                <th className={`${th} text-right`}>Req. Qty.</th>
                <th className={th}>Unit</th>
                {c.suppliers.map((s) => (
                  <Fragment key={s.rfqNo}>
                    <th className={`${th} text-right border-l border-line`}>Quantity</th>
                    <th className={th}>Unit</th>
                    <th className={`${th} text-right`}>Total (std.)</th>
                    <th className="w-5">
                      <span className="sr-only">Price trend</span>
                    </th>
                    <th className="w-6 pr-2">
                      <span className="sr-only">Order from this supplier</span>
                    </th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {!c.items.length && (
                <tr>
                  <td colSpan={6 + c.suppliers.length * 5} className="py-6 text-center text-muted">
                    No quoted lines yet.
                  </td>
                </tr>
              )}
              {c.items.map((it) => {
                const dear = itemDearest(it);
                const open = openItem.has(it.key);
                return (
                  <Fragment key={it.key}>
                    <tr className="border-b border-line">
                      <td className={`${td} pl-3 sticky left-0 bg-card`}>
                        <Expander open={open} label={`Details of position ${it.positionNo}`} onClick={() => setOpenItem(toggle(openItem, it.key))} />
                      </td>
                      <td className={`${td} text-right`}>{it.positionNo}</td>
                      <td className="px-2 py-[5px] max-w-[260px] truncate" title={it.name ?? undefined}>
                        {it.name ?? "—"}
                      </td>
                      <td className={td}>{it.itemNo}</td>
                      <td className={`${td} text-right`}>{it.requestedQuantity != null ? int(it.requestedQuantity) : ""}</td>
                      <td className={td}>{it.unit}</td>
                      {c.suppliers.map((s) => {
                        const q = it.quotes[s.rfqNo];
                        const has = priced(it, s.rfqNo);
                        const t = it.cheapest === s.rfqNo ? BEST : dear === s.rfqNo ? WORST : "";
                        const aw = s.poIssued ? AWARDED : "";
                        return (
                          <Fragment key={s.rfqNo}>
                            <td className={`${td} text-right border-l border-line ${aw}`}>{has && q!.quotedQuantity != null ? int(q!.quotedQuantity) : ""}</td>
                            <td className={`${td} ${aw}`}>{has ? it.unit : ""}</td>
                            <td className={`${td} text-right ${t} ${aw}`}>{has ? usdAmt(q!.totalUsd) : <span className="text-muted">{s.fetchedAt ? "not quoted" : ""}</span>}</td>
                            <td className={`py-[5px] ${aw}`}>{has && <Trend t={q!.priceTrend} />}</td>
                            <td className={`py-[5px] pr-2 ${aw}`}>
                              {has && (
                                <input
                                  type="checkbox"
                                  checked={picks[it.key] === s.rfqNo}
                                  onChange={(e) => setPicks({ ...picks, [it.key]: e.target.checked ? s.rfqNo : null })}
                                  className="align-middle"
                                  aria-label={`Compare ordering position ${it.positionNo} from ${supName(s)}`}
                                  title={q!.onPo && s.poIssued ? "On this supplier's PO" : q!.onPo && s.poCancelled ? "On a cancelled PO" : undefined}
                                />
                              )}
                            </td>
                          </Fragment>
                        );
                      })}
                    </tr>
                    {open && (
                      <tr className="border-b border-line bg-hover text-[12px] text-ink2">
                        <td className="sticky left-0 bg-hover" />
                        <td colSpan={5} className="px-2 py-1.5 text-muted">
                          Unit price, discount and supplier part no.
                        </td>
                        {c.suppliers.map((s) => {
                          const q = it.quotes[s.rfqNo];
                          return (
                            <td key={s.rfqNo} colSpan={5} className="px-2 py-1.5 border-l border-line whitespace-nowrap">
                              {priced(it, s.rfqNo) ? (
                                <>
                                  {amt(q!.price, s.currency)}
                                  {s.currency !== "USD" && <span className="text-muted"> · {usdAmt(q!.priceUsd)}</span>} each
                                  {q!.discount ? ` · ${q!.discount}%` : ""}
                                  {q!.supplierItemNo ? ` · ${q!.supplierItemNo}` : ""}
                                  {q!.onPo && <span className={s.poIssued ? "text-[var(--brand)]" : "text-[var(--danger)]"}> · {s.poIssued ? "on PO" : "on a cancelled PO"}</span>}
                                </>
                              ) : (
                                "—"
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {c.items.length > 0 && (
                <tr className="border-b border-line">
                  <td className="sticky left-0 bg-card" />
                  <td />
                  <td className={td}>Delivery Costs</td>
                  <td colSpan={3} />
                  {c.suppliers.map((s) => (
                    <Fragment key={s.rfqNo}>
                      <td className={`border-l border-line ${s.poIssued ? AWARDED : ""}`} />
                      <td className={s.poIssued ? AWARDED : ""} />
                      <td className={`${td} text-right ${s.poIssued ? AWARDED : ""}`}>{usdAmt(s.deliveryCostsUsd ?? (s.fetchedAt ? 0 : null))}</td>
                      <td className={s.poIssued ? AWARDED : ""} />
                      <td className={`py-[5px] pr-2 ${s.poIssued ? AWARDED : ""}`}>
                        <input type="checkbox" disabled checked={c.items.some((it) => picks[it.key] === s.rfqNo)} className="align-middle" aria-label={`Delivery costs of ${supName(s)}`} />
                      </td>
                    </Fragment>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="min-h-[24px]" />
        <CalcFooter {...pos} has={has} button="View Orders" />
      </CalcSection>
    </>
  );
}

const KIND_LABEL = { requisition: "Requisition", po: "PO", rfq: "RFQ" } as const;

/**
 * A drawer whose record is not in the store yet (SPEC §9): it is fetched from Sertica at once, with
 * the progress bar, stored for next time, and shown. Only a record Sertica does not have either
 * says so. Any other error offers a retry.
 */
function NotInStore({ type, no, error, freshness }: { type: "requisition" | "po" | "rfq"; no: string; error: unknown; freshness?: Freshness }) {
  const qc = useQueryClient();
  const missing = error instanceof ApiError && error.status === 404;
  // Set once the reload after the fetch has finished: only then is "still missing" true.
  const [checked, setChecked] = useState(false);
  const r = useRefresh(type, no, () => void qc.invalidateQueries({ queryKey: ["record"] }).then(() => setChecked(true)), freshness, missing);
  const label = `${KIND_LABEL[type]} ${no}`;
  if (!missing)
    return (
      <div className="p-4 text-[13px] space-y-2">
        <p className="text-[var(--danger)]">Couldn&apos;t load {label}.</p>
        <button className="input text-[12.5px] font-semibold" onClick={() => void qc.invalidateQueries({ queryKey: ["record"] })}>
          Retry
        </button>
      </div>
    );
  // The fetch finished and the record is still missing: Sertica does not have it.
  if (r.state === "done" && checked)
    return <div className="p-4 text-[13px] text-muted">{label} was not found in Sertica. It may have been deleted there.</div>;
  return (
    <div className="px-4 pt-3 pb-2 space-y-3">
      <div>
        <div className="label">{KIND_LABEL[type]}</div>
        <h2 className="text-[20px] font-extrabold tnum">{no}</h2>
      </div>
      <p className="text-[12.5px] text-muted">Not synced yet: fetching it from Sertica and saving it for next time.</p>
      <RefreshStatus r={r} />
    </div>
  );
}

type AttachmentInfo = { fileName: string | null; description: string | null; fileSize: number | null; contentType: string | null; stored: boolean; fetchedAt: string | null };

/**
 * An attachment (SPEC §9): fetched from Sertica on first open, with the progress bar, and kept in
 * the store so it opens straight away after. PDFs and images show here; other files download.
 */
function AttachmentBody({ a, freshness }: { a: Extract<DrawerRef, { type: "attachment" }>; freshness?: Freshness }) {
  const qc = useQueryClient();
  const path = `/attachments/${a.recordType}/${encodeURIComponent(a.recordNo)}/${encodeURIComponent(a.documentNo)}`;
  const { data: f, isLoading, isError } = useRecord<AttachmentInfo>(path);
  const r = useRefresh("attachment", `${a.recordType}:${a.recordNo}:${a.documentNo}`, () => void qc.invalidateQueries({ queryKey: ["record"] }), freshness, f ? !f.stored : false);
  const name = f?.fileName ?? a.fileName ?? `Document ${a.documentNo}`;
  // The save time in the URL: a file fetched again is never served from the browser's cache.
  const url = `/api${path}/file`;
  const view = f?.fetchedAt ? `${url}?v=${encodeURIComponent(f.fetchedAt)}` : url;
  const type = (f?.contentType ?? "").split(";")[0]!.trim().toLowerCase();
  return (
    <div className="flex flex-col h-[calc(100%-49px)]">
      <div className="px-4 pt-3 pb-2 space-y-2">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="label">Attachment</div>
            <h2 className="text-[17px] font-extrabold break-words">{name}</h2>
            {f?.description && f.description !== f.fileName && <p className="text-[12.5px] text-muted">{f.description}</p>}
          </div>
          {f?.stored && (
            <>
              <a className="input text-[12.5px] font-semibold shrink-0" href={`${url}?download`}>
                Download
              </a>
              <RefreshIcon r={r} label="Fetch the file from Sertica again" />
            </>
          )}
        </div>
        {/* While fetching or after a failure only: a finished fetch shows as the saved time below. */}
        {r.state !== "done" && <RefreshStatus r={r} />}
        {f?.stored && (
          <p className="text-[11.5px] text-muted">
            {f.fileSize != null && `${formatFileSize(f.fileSize)} · `}Saved from Sertica at {formatWib(new Date(f.fetchedAt!))}
          </p>
        )}
      </div>
      <div className="flex-1 min-h-0 px-4 pb-4">
        {isLoading ? (
          <p className="text-muted text-[13px]">Loading…</p>
        ) : isError || !f ? (
          <p className="text-muted text-[13px]">This attachment is no longer on the record. Refresh the record from Sertica.</p>
        ) : !f.stored ? (
          // The bar from the moment the drawer opens; the fetch's own progress once it reports.
          r.state === "idle" ? <ProgressBar progress={null} label="Fetching from Sertica…" className="max-w-[420px]" /> : null
        ) : type === "application/pdf" ? (
          <iframe title={name} src={view} className="w-full h-full min-h-[60vh] rounded-input border border-line bg-white" />
        ) : type.startsWith("image/") ? (
          <div className="h-full overflow-auto rounded-input border border-line bg-hover flex items-start justify-center p-2">
            <img src={view} alt={name} className="max-w-full h-auto" />
          </div>
        ) : (
          <div className="rounded-input border border-line p-4 text-[13px] space-y-2">
            <p>
              A {name.split(".").pop()?.toUpperCase() ?? "file"} file can&apos;t be shown in the browser. Download it to open it.
            </p>
            <a className="input inline-block text-[12.5px] font-semibold" href={`${url}?download`}>
              Download {name}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
